/**
 * Global AI Assistant Context
 * Manages AI chat state across all pages
 */
/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode, useRef, useMemo } from 'react';
import { conversationsApi, projectsApi, userPreferencesApi } from '../services/api';
import { AIConversationMetadata, AIConversation, PlanDocument, LockColorCode, ActiveEditDocumentState } from '../types';

/**
 * Artifact color names for visual pairing of linked tabs
 * Must match CSS variables: --artifact-{color}
 */
export type ArtifactColorName = 
  | 'blue' 
  | 'violet' 
  | 'teal' 
  | 'amber' 
  | 'grass' 
  | 'orange' 
  | 'pink' 
  | 'cyan';
import type { PagePanelId, TabType } from '../types/panel-types';
import { ConversationLink, deriveEffectiveMode, MODE_DEFAULT_MODELS, isValidModelId } from '@aerotage/shared';
import type { AIMode, ModelOverrides } from '@aerotage/shared';
import { websocketService } from '../services/websocket-service';
import { deriveWebSocketUrl } from '../services/streaming';
import { authService } from '../services/auth-service';
import type { DiagramState } from '../components/diagram/DiagramCanvas';
import { logger } from '../utils/logger';

// Debounce helper for API calls
function debounce<T extends (...args: any[]) => any>(fn: T, delay: number): T & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
  debounced.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
  };
  return debounced as T & { cancel: () => void };
}

// ===== Workspace Types =====
export type WorkspaceScope = 'project' | 'global';

export interface ActiveProject {
  projectId: string;
  projectCode: string;
  projectTitle: string;
  customer: string;
  customerId: string;
  personaId?: string;  // AI persona for this project
  projectType?: 'aerospace' | 'creative' | 'technical';  // Project type for mode selection
}

// DocumentEditLock is now imported from @aerotage/shared via '../types'
export type { DocumentEditLock } from '../types';

export interface ResolvedModelInfo {
  modelId: string;
  reason: string;
}

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  wasStopped?: boolean;  // Indicates response was stopped by user
  resolvedModel?: ResolvedModelInfo;  // Which model actually ran (from backend auto-router)
}

// Context documents for chat (shown in context bar)
export interface ContextDocument {
  id: string;
  title: string;
  type: 'document' | 'reference' | 'project' | 'active-tab';
  documentNumber?: string;
  tabId?: number; // If from an open tab
  isAutoAdded?: boolean; // True if auto-added from active tab
}

export interface PendingDocumentEdit {
  documentNumber: string;
  projectId: string;
  operations: any[];
  appliedAt: Date;
  version: string;
}

export interface PageContext {
  pageName: string;
  canEdit: boolean;
  documentContext?: {
    documentNumber?: string;
    documentId?: string;
    projectId?: string;
    currentRevision?: string;
    status?: string;
    docType?: string;
    htmlContent?: string;
    contentLength?: number;
    chunkCount?: number | null;
  };
  pageData?: Record<string, any>;
  personaId?: string;  // Active persona (from project or user default)
}

export interface StreamingProgress {
  turn: number;
  maxTurns: number;
  currentAction?: {
    text: string;      // "Searching document for requirements..."
    icon: string;      // "🔍"
    type: 'search' | 'edit' | 'validate' | 'thinking' | 'handoff' | 'develop';
  };
  activeTools: Array<{tool: string; icon: string; status: 'active' | 'complete'}>;
  // Worker delegation properties
  delegated?: boolean;  // True if job is delegated to worker
  jobId?: string;       // Worker job ID (for cancel)
  chainCount?: number;  // Current chain count
  turnCount?: number;   // Current turn in chain
  // Development progress (for 'develop' mode)
  developProgress?: DevelopProgress;
}

export interface DevelopProgress {
  phase: 'analyzing' | 'generating' | 'compiling' | 'testing' | 'fixing' | 'building' | 'deploying' | 'analyze';
  iteration: number;
  maxIterations: number;
  detail: string;
  filesModified?: string[];
  compileErrors?: number;
  testsStatus?: {
    passed: number;
    failed: number;
    total: number;
  };
  previewUrl?: string;
}

// ===== Thinking Overlay Types (moved here from useWorkspaceChat for buffer architecture) =====
export type ThinkingPhase = 'idle' | 'thinking' | 'executing' | 'solidifying' | 'complete';

export interface ThinkingActivity {
  id: string;
  type: 'planning' | 'executing' | 'complete' | 'tool_start' | 'tool_complete';
  icon: string;
  message: string;
  toolName?: string;
  timestamp: Date;
}

// ===== Per-Conversation Buffer =====
// Each open conversation tab has its own isolated buffer for messages and streaming state.
// Streams always write to their own buffer by conversationId, never the "active" one.
// Tab switching just changes which buffer is displayed.
export interface ConversationBuffer {
  conversationId: string;
  messages: Message[];
  isProcessing: boolean;
  streamingProgress: StreamingProgress | null;
  thinkingPhase: ThinkingPhase;
  thinkingActivities: ThinkingActivity[];
  previewText: string;
  debugState: { chainCount: number; turnCount: number; elapsedMs: number };
}

function createDefaultBuffer(conversationId: string): ConversationBuffer {
  return {
    conversationId,
    messages: [],
    isProcessing: false,
    streamingProgress: null,
    thinkingPhase: 'idle',
    thinkingActivities: [],
    previewText: '',
    debugState: { chainCount: 0, turnCount: 0, elapsedMs: 0 },
  };
}

interface AIContextType {
  // Chat visibility
  isOpen: boolean;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  chatWidth: number;
  setChatWidth: (width: number) => void;
  
  // WebSocket Connection
  wsConnected: boolean;
  connectWebSocket: () => Promise<void>;
  disconnectWebSocket: () => void;
  
  // Optimistic Indexing (for active editing sessions)
  pendingEdits: PendingDocumentEdit[];
  addPendingEdit: (edit: PendingDocumentEdit) => void;
  clearPendingEdits: (documentNumber: string) => void;
  getPendingEdits: (documentNumber: string) => any[];
  
  // Document editing state (for navigation protection)
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  requestNavigation: (path: string) => Promise<boolean>;
  setNavigationHandler: (handler: ((path: string) => Promise<boolean>) | null) => void;
  
  // Conversations
  activeConversationId: string | null;
  conversations: AIConversationMetadata[];
  setConversations: React.Dispatch<React.SetStateAction<AIConversationMetadata[]>>;
  conversationsLoaded: boolean;
  hasMoreConversations: boolean;
  loadingMoreConversations: boolean;
  loadConversations: () => Promise<void>;
  loadMoreConversations: () => Promise<void>;
  createConversation: (title?: string, context?: any) => Promise<string>;
  switchConversation: (id: string, addToVisibleTabs?: boolean) => Promise<void>;
  deleteConversation: (id: string, hard?: boolean) => Promise<void>;
  renameConversation: (id: string, title: string) => Promise<void>;
  updateConversationTitle: (id: string, title: string) => void;
  
  // Visible Conversation Tabs (which tabs are shown in the tab bar)
  visibleTabIds: string[];
  addVisibleTab: (id: string) => void;
  removeVisibleTab: (id: string, fallbackToNext?: boolean) => void;
  reorderVisibleTabs: (ids: string[]) => void;
  
  // Per-Conversation Buffers (Phase 1 of conversation-tab-routing fix)
  conversationBuffers: Map<string, ConversationBuffer>;
  ensureBuffer: (conversationId: string) => void;
  getBuffer: (conversationId: string) => ConversationBuffer;
  
  // Messages -- all scoped by conversationId (required)
  messages: Message[];  // Derived: active conversation's messages (empty if none selected)
  addMessage: (conversationId: string, message: Message) => void;
  updateLastMessage: (conversationId: string, content: string) => void;
  setLastMessageResolvedModel: (conversationId: string, resolvedModel: ResolvedModelInfo) => void;
  markLastMessageStopped: (conversationId: string) => void;
  removeMessagesFromIndex: (conversationId: string, idx: number) => void;
  removeLastMessage: (conversationId: string) => void;
  clearMessages: (conversationId: string) => void;
  prependMessages: (conversationId: string, messages: Message[]) => void;
  setBufferMessages: (conversationId: string, messages: Message[]) => void;
  
  // AI State -- scoped by conversationId where applicable
  isProcessing: boolean;  // Derived: active conversation's isProcessing
  anyConversationProcessing: boolean;  // True if ANY conversation is streaming
  setIsProcessing: (conversationId: string, processing: boolean) => void;
  aiMode: 'ask' | 'agent' | 'plan' | 'diagram' | 'develop';
  setAiMode: (mode: 'ask' | 'agent' | 'plan' | 'diagram' | 'develop') => void;
  selectedModel: string;
  setSelectedModel: (model: string) => void;
  
  // Streaming progress -- scoped by conversationId
  streamingProgress: StreamingProgress | null;  // Derived: active conversation's progress
  setStreamingProgress: (conversationId: string, progress: StreamingProgress | null | ((prev: StreamingProgress | null) => StreamingProgress | null)) => void;
  
  // Thinking overlay state -- scoped by conversationId
  thinkingPhase: ThinkingPhase;  // Derived: active conversation's thinkingPhase
  thinkingActivities: ThinkingActivity[];  // Derived: active conversation's thinkingActivities
  previewText: string;  // Derived: active conversation's previewText
  debugState: { chainCount: number; turnCount: number; elapsedMs: number };  // Derived
  setThinkingPhase: (conversationId: string, phase: ThinkingPhase) => void;
  addThinkingActivity: (conversationId: string, activity: ThinkingActivity) => void;
  updateThinkingActivities: (conversationId: string, updater: (prev: ThinkingActivity[]) => ThinkingActivity[]) => void;
  clearThinkingActivities: (conversationId: string) => void;
  setPreviewText: (conversationId: string, text: string) => void;
  setDebugState: (conversationId: string, state: { chainCount: number; turnCount: number; elapsedMs: number }) => void;
  
  // Stop generation -- scoped by conversationId
  registerStopGeneration: (conversationId: string, fn: (() => Promise<void>) | null) => void;
  stopGeneration: (conversationId: string) => Promise<void>;
  
  // Page Context
  pageContext: PageContext;
  setPageContext: (context: PageContext) => void;
  
  // Referenced Documents
  referencedDocuments: any;
  setReferencedDocuments: (docs: any) => void;
  
  // Edit application callback (set by document editor)
  onApplyEdits?: (edits: any[], messageContent: string) => void;
  setOnApplyEdits: (callback: ((edits: any[], messageContent: string) => void) | undefined) => void;
  
  // Document Scopes
  allowedScopes: string[];
  allowedProjectIds: string[];
  updateDocumentScopes: (scopes: string[], projectIds: string[]) => Promise<void>;
  
  // Link-Based Architecture: conversation links to artifacts
  conversationLinks: ConversationLink[];
  linkArtifact: (artifactType: ConversationLink['artifactType'], artifactId: string, artifactName: string, projectId: string, conversationId?: string) => Promise<void>;
  unlinkArtifact: (artifactId: string) => Promise<void>;
  unlinkAllArtifacts: () => Promise<void>;
  unlinkConversation: (conversationId: string) => Promise<void>;
  effectiveMode: 'ask' | 'agent' | 'plan' | 'diagram' | 'develop';  // Derived from links or fallback to aiMode
  hasLinks: boolean;  // Quick check if conversation has any links
  
  // Opened Tabs (for Workspace viewer - documents, plans, page panels, and diagrams)
  openedDocuments: Array<{
    id: string;
    tabId: number;
    title: string;
    tabType: TabType; // 'document' | 'plan' | 'page' | 'diagram'
    // Document-specific fields
    documentNumber?: string;
    projectId?: string;
    type?: 'project' | 'company' | 'standard' | 'prior-project';
    canEdit?: boolean;
    jsonDocument?: any;
    version?: string;
    // Page panel-specific fields
    panelType?: PagePanelId;
    searchQuery?: string;
    // Plan-specific fields
    planDocument?: PlanDocument;
    // Diagram-specific fields
    diagramId?: string;
    // Mermaid/flowchart-specific fields
    mermaidDiagramId?: string;
    mermaidType?: string;
    svgUrl?: string;
  }>;
  openPagePanel: (panelId: PagePanelId, searchQuery?: string) => void;
  activeTabId: number | null;
  setActiveTabId: (tabId: number | null) => void;
  activeEditDocument: {
    tabId: number;
    documentNumber: string;
    projectId: string;
    jsonDocument?: any; // AeroDocument
    version: string;
    s3VersionId?: string; // S3 version ID for undo functionality
  } | null;
  addOpenedDocument: (doc: any) => void;
  updateOpenedDocument: (tabId: number, updates: Partial<{ jsonDocument: any; version: string; s3VersionId: string; s3JsonKey: string }>) => void;
  removeOpenedDocument: (tabId: number) => void;
  reorderOpenedDocuments: (fromIndex: number, toIndex: number) => void;
  clearOpenedDocuments: () => void;
  setActiveEditDocument: (doc: any) => void;
  clearActiveEditDocument: () => void;
  
  // Context Documents (for chat context bar)
  contextDocuments: ContextDocument[];
  addContextDocument: (doc: ContextDocument) => void;
  removeContextDocument: (id: string) => void;
  clearContextDocuments: () => void;
  
  // Diagram State (for DIAGRAM mode)
  diagramState: DiagramState | null;
  setDiagramState: (state: DiagramState | null) => void;
  activeDiagramId: string | null;
  setActiveDiagramId: (id: string | null) => void;
  // Refresh diagram state from backend (useful after AI makes changes)
  refreshDiagramState: (projectId: string, diagramId: string) => Promise<void>;
  
  // Mermaid diagram update tracking (for flowcharts)
  // Maps mermaidDiagramId -> latest version/timestamp to trigger refetch
  mermaidVersions: Record<string, number>;
  setMermaidVersion: (mermaidDiagramId: string, version: number) => void;
  
  // Coding project update tracking (for AI development)
  // Maps codingProjectId -> latest version/timestamp to trigger refetch
  codingProjectVersions: Record<string, number>;
  setCodingProjectVersion: (codingProjectId: string, version: number) => void;
  
  // Project Workspace State
  activeProject: ActiveProject | null;
  workspaceScope: WorkspaceScope;
  switchProject: (projectId: string | null) => Promise<void>;
  
  // Project Switcher Modal (global trigger for ⌘P)
  projectSwitcherOpen: boolean;
  openProjectSwitcher: () => void;
  closeProjectSwitcher: () => void;
  
  // Project Create Modal (triggered from Project Switcher)
  projectCreateModalOpen: boolean;
  openProjectCreateModal: () => void;
  closeProjectCreateModal: () => void;
  
  // Document Edit Locks (for Agent mode exclusive access)
  documentLocks: DocumentEditLock[];
  acquireDocumentLock: (documentId: string, conversationId: string, conversationTitle: string) => Promise<DocumentEditLock | null>;
  releaseDocumentLock: (documentId: string) => void;
  getDocumentLock: (documentId: string) => DocumentEditLock | undefined;
  isDocumentLocked: (documentId: string) => boolean;
  isDocumentLockedByMe: (documentId: string, conversationId: string) => boolean;
  /** Transfer a document lock to a different conversation */
  transferDocumentLock: (documentId: string, toConversationId: string, toConversationTitle: string) => boolean;
  
  // Artifact Color Assignment (for visual pairing of linked tabs)
  artifactColorMap: Map<string, ArtifactColorName>;
  assignArtifactColor: (artifactId: string) => ArtifactColorName;
  clearArtifactColors: (artifactIds: string[]) => void;
}

const AIContext = createContext<AIContextType | undefined>(undefined);

export function AIProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem('aiChatOpen');
    return saved === 'true';
  });
  
  const [chatWidth, setChatWidth] = useState(() => {
    const saved = localStorage.getItem('globalAIChatWidth');
    return saved ? parseInt(saved, 10) : 400;
  });
  
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<AIConversationMetadata[]>([]);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [conversationsNextToken, setConversationsNextToken] = useState<string | undefined>();
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [visibleTabIds, setVisibleTabIds] = useState<string[]>([]);
  const visibleTabIdsRef = useRef<string[]>([]); // Ref for accessing current tabs in callbacks
  // messages state removed -- now inside conversationBuffers (per-conversation)
  const preferencesLoadedRef = useRef(false); // Track if we've loaded from API
  const activeConversationIdRef = useRef<string | null>(null); // Ref for debounced saves
  // NOTE: activeEditDocumentRef removed - activeEditDocument is now derived from openedDocuments + activeTabId
  const [userDefaultPersonaId, setUserDefaultPersonaId] = useState<string | undefined>(); // User's default persona from preferences
  
  // Pending preferences to save (for flush on unload)
  const pendingPreferencesRef = useRef<{ openIds: string[]; activeId: string | null } | null>(null);
  
  // Debounced save preferences to API (500ms delay to batch rapid changes)
  const debouncedSavePreferencesRef = useRef(
    debounce(async (openIds: string[], activeId: string | null) => {
      try {
        await userPreferencesApi.update({
          openConversationIds: openIds,
          activeConversationId: activeId,
        });
        logger.debug('[AIContext] Saved preferences to API');
        pendingPreferencesRef.current = null; // Clear pending after successful save
      } catch (error) {
        logger.warn('[AIContext] Failed to save preferences to API:', error);
      }
    }, 500)
  );
  
  // Track pending saves for flush on unload
  const queuePreferencesSave = useCallback((openIds: string[], activeId: string | null) => {
    pendingPreferencesRef.current = { openIds, activeId };
    debouncedSavePreferencesRef.current(openIds, activeId);
  }, []);
  
  // Flush pending preferences on visibility change (more reliable than beforeunload)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && pendingPreferencesRef.current) {
        const { openIds, activeId } = pendingPreferencesRef.current;
        // Fire immediately when page is being hidden
        debouncedSavePreferencesRef.current.cancel();
        userPreferencesApi.update({ openConversationIds: openIds, activeConversationId: activeId })
          .then(() => {
            logger.debug('[AIContext] Flushed preferences on visibility hidden');
            pendingPreferencesRef.current = null;
          })
          .catch(err => logger.warn('[AIContext] Failed to flush preferences:', err));
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  
  // Keep refs in sync with state for use in callbacks
  useEffect(() => {
    visibleTabIdsRef.current = visibleTabIds;
  }, [visibleTabIds]);
  
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);
  
  // ===== Per-Conversation Buffer Map =====
  // Each conversation has its own isolated buffer.
  // Streams always write to their buffer by conversationId, never to a "global" state.
  const [conversationBuffers, setConversationBuffers] = useState<Map<string, ConversationBuffer>>(new Map());

  // Stop generation callbacks, keyed by conversationId
  const stopGenerationRefs = useRef<Map<string, () => Promise<void>>>(new Map());
  const [aiMode, setAiModeState] = useState<AIMode>('ask');
  
  // Per-conversation, per-mode model overrides
  // Map: conversationId -> { mode -> modelId }
  // Persisted to DynamoDB via conversation.settings.modelOverrides
  const [conversationModelOverrides, setConversationModelOverrides] = useState<
    Record<string, ModelOverrides>
  >({});
  const [pageContext, setPageContext] = useState<PageContext>({
    pageName: 'Unknown',
    canEdit: false,
  });
  
  // streamingProgress is now per-conversation inside ConversationBuffer (not a standalone state)
  const [referencedDocuments, setReferencedDocuments] = useState<any>(null);
  const [onApplyEdits, setOnApplyEdits] = useState<((edits: any[], messageContent: string) => void) | undefined>(undefined);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [navigationHandler, setNavigationHandler] = useState<((path: string) => Promise<boolean>) | null>(null);
  
  // Document Scopes - default to safe scopes
  const [allowedScopes, setAllowedScopes] = useState<string[]>([
    'current-project',
    'industry-standards',
    'company-documents',
  ]);
  const [allowedProjectIds, setAllowedProjectIds] = useState<string[]>([]);
  
  // Link-Based Architecture: conversation links to artifacts
  const [conversationLinks, setConversationLinks] = useState<ConversationLink[]>([]);
  
  // Base effective mode from links (activeProject-aware version is computed later via useMemo)
  // This is the initial computation without creative project awareness
  const baseEffectiveMode = (() => {
    if (conversationLinks.length === 0) {
      return aiMode;
    }
    const derivedMode = deriveEffectiveMode(conversationLinks);
    // Map link-derived modes to backend-compatible aiMode values
    switch (derivedMode) {
      case 'document-edit':
      case 'ssa-edit':
        return 'agent';  // Document and SSA editing use agent mode
      case 'diagram-edit':
        return 'diagram';  // Diagram (wiring + flowchart) uses diagram mode
      case 'plan-edit':
        return 'plan';
      case 'coding-develop':
        return 'develop';  // Coding project uses develop mode
      case 'multi-edit':
        return 'agent';  // Multi-edit defaults to agent for flexibility
      case 'read-only':
      default:
        return 'ask';
    }
  })();
  const hasLinks = conversationLinks.length > 0;
  
  // Link artifact to conversation
  // Accepts optional conversationId for cases where we just created a conversation 
  // and React state hasn't updated yet
  const linkArtifact = useCallback(async (
    artifactType: ConversationLink['artifactType'],
    artifactId: string,
    artifactName: string,
    projectId: string,
    conversationId?: string
  ) => {
    const targetConversationId = conversationId || activeConversationId;
    
    if (!targetConversationId) {
      logger.warn('[AIContext] Cannot link artifact: no conversation ID available');
      return;
    }
    
    // Duplicate link prevention: check if this artifact is already linked to this conversation
    const existingConv = conversations.find(c => c.conversationId === targetConversationId);
    if (existingConv?.links?.some(l => l.artifactId === artifactId)) {
      logger.debug('[AIContext] Artifact already linked to this conversation, skipping:', { artifactId, targetConversationId });
      return;
    }
    
    try {
      const result = await conversationsApi.linkArtifact(targetConversationId, {
        artifactType,
        artifactId,
        artifactName,
        projectId,
      });
      
      // Update local state
      const newLink: ConversationLink = {
        artifactType,
        artifactId,
        artifactName,
        projectId,
        linkedAt: new Date().toISOString(),
        linkedBy: 'current-user',  // Will be filled by backend
      };
      
      // Update both states: conversationLinks AND conversations array
      setConversationLinks(prev => [...prev, newLink]);
      setConversations(prev => prev.map(c => 
        c.conversationId === targetConversationId 
          ? { ...c, links: [...(c.links || []), newLink], linkStatus: 'linked' as const }
          : c
      ));
      
      logger.debug('[AIContext] Linked artifact:', { artifactType, artifactId, conversationId: targetConversationId, result });
    } catch (error) {
      logger.error('[AIContext] Failed to link artifact:', error);
      throw error;
    }
  }, [activeConversationId, conversations]);
  
  // Unlink artifact from conversation
  const unlinkArtifact = useCallback(async (artifactId: string) => {
    if (!activeConversationId) {
      logger.warn('[AIContext] Cannot unlink artifact: no active conversation');
      return;
    }
    
    try {
      await conversationsApi.unlinkArtifact(activeConversationId, artifactId);
      setConversationLinks(prev => prev.filter(l => l.artifactId !== artifactId));
      
      // Remove artifact color when link is broken
      setArtifactColorMap(prev => {
        const newMap = new Map(prev);
        if (newMap.delete(artifactId)) {
          logger.debug('[AIContext] Removed artifact color on unlink:', artifactId);
        }
        return newMap;
      });
      
      logger.debug('[AIContext] Unlinked artifact:', artifactId);
    } catch (error) {
      logger.error('[AIContext] Failed to unlink artifact:', error);
      throw error;
    }
  }, [activeConversationId]);
  
  // Unlink all artifacts from conversation
  const unlinkAllArtifacts = useCallback(async () => {
    if (!activeConversationId) {
      logger.warn('[AIContext] Cannot unlink all: no active conversation');
      return;
    }
    
    const conversationIdToUnlink = activeConversationId;
    logger.debug('[AIContext] Unlinking conversation:', conversationIdToUnlink);
    
    // Get current links to remove their colors
    const linksToRemove = [...conversationLinks];
    
    try {
      await conversationsApi.unlinkAll(conversationIdToUnlink);
      
      // Update both states: conversationLinks AND conversations array
      setConversationLinks([]);
      setConversations(prev => {
        const updated = prev.map(c => 
          c.conversationId === conversationIdToUnlink 
            ? { ...c, links: [], linkStatus: 'unlinked' as const }
            : c
        );
        logger.debug('[AIContext] Updated conversations array, found match:', 
          prev.some(c => c.conversationId === conversationIdToUnlink));
        return updated;
      });
      
      // Remove artifact colors for all unlinked artifacts
      setArtifactColorMap(prev => {
        const newMap = new Map(prev);
        linksToRemove.forEach(link => {
          if (newMap.delete(link.artifactId)) {
            logger.debug('[AIContext] Removed artifact color on unlink all:', link.artifactId);
          }
        });
        return newMap;
      });
      
      logger.debug('[AIContext] Unlinked all artifacts from:', conversationIdToUnlink);
    } catch (error) {
      logger.error('[AIContext] Failed to unlink all artifacts:', error);
      throw error;
    }
  }, [activeConversationId, conversationLinks]);
  
  // Unlink any conversation (not just active) - for context menu usage
  const unlinkConversation = useCallback(async (conversationId: string) => {
    const conv = conversations.find(c => c.conversationId === conversationId);
    if (!conv || !conv.links?.length) {
      logger.warn('[AIContext] Cannot unlink: no links found for conversation', conversationId);
      return;
    }
    
    // Get artifact IDs to clear colors
    const artifactIds = conv.links.map(l => l.artifactId);
    
    try {
      // Call API to break the link in the database
      await conversationsApi.unlinkAll(conversationId);
      
      // Update conversations array
      setConversations(prev => prev.map(c => 
        c.conversationId === conversationId 
          ? { ...c, links: [], linkStatus: 'unlinked' as const }
          : c
      ));
      
      // If this is the active conversation, also clear conversationLinks
      if (conversationId === activeConversationId) {
        setConversationLinks([]);
      }
      
      // Clear artifact colors so checkmarks and styling are removed
      setArtifactColorMap(prev => {
        const newMap = new Map(prev);
        artifactIds.forEach(id => {
          if (newMap.delete(id)) {
            logger.debug('[AIContext] Cleared artifact color:', id);
          }
        });
        return newMap;
      });
      
      logger.debug('[AIContext] Unlinked conversation:', conversationId, 'artifacts:', artifactIds);
    } catch (error) {
      logger.error('[AIContext] Failed to unlink conversation:', error);
      throw error;
    }
  }, [conversations, activeConversationId]);
  
  // Opened Tabs (for Workspace viewer) - restore from localStorage
  const [openedDocuments, setOpenedDocuments] = useState<Array<{
    id: string;
    tabId: number;
    title: string;
    tabType: TabType;
    documentNumber?: string;
    projectId?: string;
    type?: 'project' | 'company' | 'standard' | 'prior-project';
    canEdit?: boolean;
    status?: string;  // Document status: Draft, In Review, Approved, Released
    currentRevision?: string;  // Current revision: Draft, A, B, etc.
    jsonDocument?: any;
    version?: string;
    s3VersionId?: string; // S3 version ID for undo functionality
    s3JsonKey?: string;   // S3 path for document save
    panelType?: PagePanelId;
    searchQuery?: string;
    planDocument?: PlanDocument;
    // Diagram-specific fields
    diagramId?: string;
    // Mermaid/flowchart-specific fields
    mermaidDiagramId?: string;
    mermaidType?: string;
    svgUrl?: string;
    // Coding project-specific fields
    codingProjectId?: string;
    // Per-document edit state - each document independently tracks if it's in edit mode
    isEditing?: boolean;
  }>>(() => {
    try {
      // Check if there's an active project - if so, restore from project-specific storage
      const activeProjectSaved = localStorage.getItem('activeWorkspaceProject');
      const storageKey = 'aeroOpenedDocuments'; // Default to global
      
      if (activeProjectSaved) {
        try {
          const project = JSON.parse(activeProjectSaved);
          if (project?.projectId) {
            // Use project-specific storage
            const projectKey = `workspace-tabs-${project.projectId}`;
            const projectSaved = localStorage.getItem(projectKey);
            if (projectSaved) {
              const parsed = JSON.parse(projectSaved);
              const openTabs = parsed.openTabs || [];
              logger.debug('[AIContext] Restored', openTabs.length, 'tabs for project:', project.projectId);
              return openTabs.map((d: any) => ({
                id: d.id || d.documentId || `doc-${d.tabId}`,
                tabId: d.tabId,
                title: d.title || 'Untitled',
                tabType: d.tabType || 'document',
                documentNumber: d.documentNumber || d.documentId,
                projectId: d.projectId,
                type: d.type || 'project',
                canEdit: d.canEdit,
                panelType: d.panelType,
                searchQuery: d.searchQuery,
                // Diagram-specific fields
                diagramId: d.diagramId,
                // Mermaid/flowchart-specific fields
                mermaidDiagramId: d.mermaidDiagramId,
                mermaidType: d.mermaidType,
                // Coding project-specific fields
                codingProjectId: d.codingProjectId,
              }));
            }
            // No project-specific tabs, return empty (will show project tab)
            return [];
          }
        } catch (e) {
          logger.warn('[AIContext] Failed to parse active project for tab restore:', e);
        }
      }
      
      // Fall back to global storage (for global mode)
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Restore all document/plan/page tabs
        // Migrate old format (no tabType) to new format
        // Plans are now persisted like regular documents (they use the same infrastructure)
        return parsed
          .map((d: any) => ({
            ...d,
            tabType: d.tabType || (d.panelType ? 'page' : 'document'),
          }));
      }
    } catch (e) {
      logger.warn('[AIContext] Failed to restore opened documents:', e);
    }
    return [];
  });
  
  // NOTE: activeEditDocument is derived below, after activeTabId is declared
  
  // Context Documents (for chat context bar)
  const [contextDocuments, setContextDocuments] = useState<ContextDocument[]>([]);
  
  // Diagram State (for DIAGRAM mode)
  const [diagramState, setDiagramState] = useState<DiagramState | null>(null);
  const [activeDiagramId, setActiveDiagramId] = useState<string | null>(null);
  
  // Mermaid diagram update tracking (for flowcharts)
  // Maps mermaidDiagramId -> latest version/timestamp to trigger refetch
  const [mermaidVersions, setMermaidVersions] = useState<Record<string, number>>({});
  
  const setMermaidVersion = useCallback((mermaidDiagramId: string, version: number) => {
    logger.debug('[AIContext] Setting mermaid version:', { mermaidDiagramId, version });
    setMermaidVersions(prev => ({ ...prev, [mermaidDiagramId]: version }));
  }, []);
  
  // Coding project update tracking (for AI development)
  // Maps codingProjectId -> latest version/timestamp to trigger refetch
  const [codingProjectVersions, setCodingProjectVersions] = useState<Record<string, number>>({});
  
  const setCodingProjectVersion = useCallback((codingProjectId: string, version: number) => {
    logger.debug('[AIContext] Setting coding project version:', { codingProjectId, version });
    setCodingProjectVersions(prev => ({ ...prev, [codingProjectId]: version }));
  }, []);
  
  // Project Workspace State
  const [activeProject, setActiveProject] = useState<ActiveProject | null>(() => {
    try {
      const saved = localStorage.getItem('activeWorkspaceProject');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      logger.warn('[AIContext] Failed to restore active project:', e);
    }
    return null;
  });
  
  // Project Switcher Modal State (global trigger for ⌘P)
  const [projectSwitcherOpen, setProjectSwitcherOpen] = useState(false);
  const openProjectSwitcher = useCallback(() => setProjectSwitcherOpen(true), []);
  const closeProjectSwitcher = useCallback(() => setProjectSwitcherOpen(false), []);
  
  // Project Create Modal State (triggered from Project Switcher)
  const [projectCreateModalOpen, setProjectCreateModalOpen] = useState(false);
  const openProjectCreateModal = useCallback(() => setProjectCreateModalOpen(true), []);
  const closeProjectCreateModal = useCallback(() => setProjectCreateModalOpen(false), []);
  
  const [workspaceScope, setWorkspaceScope] = useState<WorkspaceScope>(() => {
    return activeProject ? 'project' : 'global';
  });
  
  // Compute final effectiveMode with creative project awareness
  // Creative projects default to 'agent' mode to enable document creation wizard
  const effectiveMode = useMemo(() => {
    // If creative project and no links, default to agent mode for document creation
    if (activeProject?.projectType === 'creative') {
      if (conversationLinks.length === 0 || baseEffectiveMode === 'ask') {
        return 'agent';
      }
    }
    return baseEffectiveMode;
  }, [activeProject?.projectType, conversationLinks.length, baseEffectiveMode]);
  
  // Derived selectedModel — computed from effectiveMode + user overrides.
  // Eliminates the stale-state bug where mode changes didn't sync the model.
  const selectedModel = useMemo(() => {
    const convId = activeConversationId;
    if (convId) {
      const override = conversationModelOverrides[convId]?.[effectiveMode];
      if (override && isValidModelId(override)) {
        return override;
      }
    }
    return MODE_DEFAULT_MODELS[effectiveMode]; // 'auto' for all modes
  }, [activeConversationId, effectiveMode, conversationModelOverrides]);
  
  // Document Edit Locks (client-side tracking for Agent mode)
  const [documentLocks, setDocumentLocks] = useState<DocumentEditLock[]>([]);
  
  // =====================================================================
  // Artifact Color Assignment (for visual pairing of linked tabs)
  // =====================================================================
  const [artifactColorMap, setArtifactColorMap] = useState<Map<string, ArtifactColorName>>(() => new Map());
  
  const ARTIFACT_COLOR_POOL: ArtifactColorName[] = [
    'blue', 'violet', 'teal', 'amber', 'grass', 'orange', 'pink', 'cyan'
  ];
  
  // Simple hash function for consistent color assignment
  // Same artifact ID always gets the same color
  const hashArtifactId = (id: string): number => {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      const char = id.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  };
  
  const assignArtifactColor = useCallback((artifactId: string): ArtifactColorName => {
    // Check if already assigned in current session
    const existing = artifactColorMap.get(artifactId);
    if (existing) {
      return existing;
    }
    
    // Use consistent hash of artifact ID for color selection
    // Same document always gets the same color, even after unlink/relink
    const colorIndex = hashArtifactId(artifactId) % ARTIFACT_COLOR_POOL.length;
    const color = ARTIFACT_COLOR_POOL[colorIndex];
    
    // Update state
    setArtifactColorMap(prev => {
      const newMap = new Map(prev);
      newMap.set(artifactId, color);
      return newMap;
    });
    
    logger.debug('[AIContext] Assigned artifact color:', { artifactId, color, colorIndex });
    return color;
  }, [artifactColorMap]);
  
  // Remove artifact color when link is broken (available for external use if needed)
  const _removeArtifactColor = useCallback((artifactId: string) => {
    setArtifactColorMap(prev => {
      const newMap = new Map(prev);
      if (newMap.delete(artifactId)) {
        logger.debug('[AIContext] Removed artifact color:', { artifactId });
      }
      return newMap;
    });
  }, []);
  
  // Clear multiple artifact colors at once (for batch unlink)
  const clearArtifactColors = useCallback((artifactIds: string[]) => {
    setArtifactColorMap(prev => {
      const newMap = new Map(prev);
      artifactIds.forEach(id => {
        if (newMap.delete(id)) {
          logger.debug('[AIContext] Cleared artifact color:', id);
        }
      });
      return newMap;
    });
  }, []);
  
  // =====================================================================
  // Model Selection with Per-Conversation Sticky Behavior
  // =====================================================================
  
  /**
   * Set AI mode — selectedModel updates automatically via useMemo derivation.
   */
  const setAiMode = useCallback((mode: AIMode) => {
    setAiModeState(mode);
    logger.debug(`[AIContext] Mode changed to ${mode}`);
  }, []);
  
  /**
   * Set selected model with per-conversation sticky behavior.
   * Records the override in local state AND persists to DynamoDB.
   * selectedModel is derived from conversationModelOverrides, so updating
   * the overrides map triggers a re-render with the correct value.
   */
  const setSelectedModel = useCallback((modelId: string) => {
    if (!activeConversationId) return;
    
    const newOverrides: ModelOverrides = {
      ...conversationModelOverrides[activeConversationId],
      [effectiveMode]: modelId,
    };
    
    // If user picks 'auto' (the default), remove the override for this mode
    if (modelId === MODE_DEFAULT_MODELS[effectiveMode]) {
      delete newOverrides[effectiveMode];
    }
    
    setConversationModelOverrides(prev => ({
      ...prev,
      [activeConversationId]: newOverrides,
    }));
    
    // Persist to DynamoDB so overrides survive hard refresh
    conversationsApi.update(activeConversationId, {
      settings: { modelOverrides: newOverrides },
    }).catch(err => {
      logger.warn('[AIContext] Failed to persist model override:', err);
    });
    
    logger.debug(`[AIContext] Model override saved for conversation ${activeConversationId}, mode ${effectiveMode}: ${modelId}`);
  }, [activeConversationId, effectiveMode, conversationModelOverrides]);
  
  // Color rotation for lock visual pairing
  const lockColorRotation = useRef<LockColorCode[]>(['blue', 'green', 'purple', 'orange', 'pink', 'cyan']);
  const nextColorIndex = useRef(0);
  
  // Ref to store document ID for auto-focus after conversation switch
  // (switchConversation is defined before activeTabId state, so we use a ref + effect pattern)
  const pendingAutoFocusDocRef = useRef<string | null>(null);
  
  const getNextLockColor = useCallback((): LockColorCode => {
    const color = lockColorRotation.current[nextColorIndex.current];
    nextColorIndex.current = (nextColorIndex.current + 1) % lockColorRotation.current.length;
    return color;
  }, []);
  
  // Refresh diagram state from backend
  const refreshDiagramState = useCallback(async (projectId: string, diagramId: string) => {
    logger.debug('[AIContext] Refreshing diagram state for:', diagramId);
    try {
      const state = await projectsApi.getDiagramState(projectId, diagramId);
      logger.debug('[AIContext] Refreshed diagram state:', state.devices?.length || 0, 'devices,', state.wires?.length || 0, 'wires');
      setDiagramState({
        devices: state.devices || [],
        wires: state.wires || [],
        crossings: state.crossings || [],
      });
    } catch (error) {
      logger.error('[AIContext] Failed to refresh diagram state:', error);
    }
  }, []);
  
  // Switch project workspace - defined after state declarations
  // (Implementation moved to after activeTabId and openedDocuments are declared)
  
  // Document Lock Management
  const acquireDocumentLock = useCallback(async (
    documentId: string,
    conversationId: string,
    conversationTitle: string
  ): Promise<DocumentEditLock | null> => {
    logger.debug('[AIContext] acquireDocumentLock called:', { documentId, conversationId, conversationTitle });
    
    // Use a promise to handle the state update and return the lock
    return new Promise((resolve) => {
      setDocumentLocks(prev => {
        logger.debug('[AIContext] acquireDocumentLock checking existing locks:', prev.length);
        
        // Check if document is already locked
        const existingLock = prev.find(l => l.documentId === documentId);
        
        if (existingLock) {
          if (existingLock.lockedByConversationId !== conversationId) {
            // Locked by another conversation
            logger.warn('[AIContext] Document already locked by:', existingLock.lockedByConversationTitle);
            resolve(null);
            return prev; // No change
          } else {
            // Already locked by this conversation - return existing
            logger.debug('[AIContext] Document already locked by this conversation, reusing lock');
            resolve(existingLock);
            return prev; // No change
          }
        }
        
        // Create new lock
        const newLock: DocumentEditLock = {
          documentId,
          projectId: activeProject?.projectId || '',
          lockedByConversationId: conversationId,
          lockedByConversationTitle: conversationTitle,
          lockedByUserId: '', // TODO: Get from auth
          lockedByUserName: '', // TODO: Get from auth
          lockedAt: new Date().toISOString(),
          colorCode: getNextLockColor(),
          expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(), // 30 min
        };
        
        logger.debug('[AIContext] Acquired document lock:', documentId, 'color:', newLock.colorCode);
        resolve(newLock);
        return [...prev, newLock];
      });
    });
  }, [activeProject, getNextLockColor]);
  
  const releaseDocumentLock = useCallback((documentId: string) => {
    setDocumentLocks(prev => {
      const newLocks = prev.filter(l => l.documentId !== documentId);
      logger.debug('[AIContext] Released document lock:', documentId);
      return newLocks;
    });
  }, []);
  
  const getDocumentLock = useCallback((documentId: string): DocumentEditLock | undefined => {
    return documentLocks.find(l => l.documentId === documentId);
  }, [documentLocks]);
  
  const isDocumentLocked = useCallback((documentId: string): boolean => {
    return documentLocks.some(l => l.documentId === documentId);
  }, [documentLocks]);
  
  const isDocumentLockedByMe = useCallback((documentId: string, conversationId: string): boolean => {
    const lock = documentLocks.find(l => l.documentId === documentId);
    return lock?.lockedByConversationId === conversationId;
  }, [documentLocks]);
  
  /**
   * Transfer a document lock from one conversation to another
   * Used when user wants to edit a document in a different conversation
   */
  const transferDocumentLock = useCallback((
    documentId: string,
    toConversationId: string,
    toConversationTitle: string
  ): boolean => {
    const existingLock = documentLocks.find(l => l.documentId === documentId);
    if (!existingLock) {
      logger.warn('[AIContext] Cannot transfer lock - document not locked:', documentId);
      return false;
    }
    
    logger.debug('[AIContext] Transferring document lock:', {
      documentId,
      from: existingLock.lockedByConversationTitle,
      to: toConversationTitle
    });
    
    setDocumentLocks(prev => prev.map(lock =>
      lock.documentId === documentId
        ? {
            ...lock,
            lockedByConversationId: toConversationId,
            lockedByConversationTitle: toConversationTitle,
            lockedAt: new Date().toISOString(),
            expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
          }
        : lock
    ));
    
    return true;
  }, [documentLocks]);
  
  // Helper function to serialize a tab for localStorage storage
  // Used by both the save useEffect and switchProject to ensure consistency
  const serializeTabForStorage = useCallback((d: typeof openedDocuments[0]) => ({
    id: d.id,
    tabId: d.tabId,
    documentNumber: d.documentNumber,
    documentId: d.documentNumber, // Alias for backwards compatibility
    tabType: d.tabType,
    title: d.title,
    projectId: d.projectId,
    type: d.type,
    canEdit: d.canEdit,
    // Page panel specific
    panelType: d.panelType,
    searchQuery: d.searchQuery,
    // Diagram-specific fields
    diagramId: d.diagramId,
    // Mermaid/flowchart-specific fields
    mermaidDiagramId: d.mermaidDiagramId,
    mermaidType: d.mermaidType,
    // Coding project-specific fields
    codingProjectId: d.codingProjectId,
    // Note: htmlContent and jsonDocument are intentionally not saved to keep localStorage small
  }), []);
  
  // Persist opened documents to localStorage (exclude jsonDocument to save space)
  // Uses project-specific storage when a project is active
  // Note: activeTabId is saved separately below (after its declaration)
  // Plans are now persisted like regular documents (they use the same infrastructure)
  useEffect(() => {
    try {
      const toSave = openedDocuments
        .map(({ jsonDocument: _jsonDocument, ...rest }) => rest); // Exclude jsonDocument (large)
      
      if (activeProject) {
        // Save to project-specific storage
        const workspaceKey = `workspace-tabs-${activeProject.projectId}`;
        // Read existing state to preserve activeTabId
        let existingActiveTabId = null;
        try {
          const existing = localStorage.getItem(workspaceKey);
          if (existing) {
            existingActiveTabId = JSON.parse(existing).activeTabId;
          }
        } catch (_e) { /* ignore */ }
        
        const openTabs = toSave.map(serializeTabForStorage);
        const currentState = {
          openTabs,
          activeTabId: existingActiveTabId, // Preserve until updated by activeTabId effect
          lastAccessedAt: new Date().toISOString(),
        };
        localStorage.setItem(workspaceKey, JSON.stringify(currentState));
      } else {
        // Save to global storage when no project is active
        localStorage.setItem('aeroOpenedDocuments', JSON.stringify(toSave));
      }
    } catch (e) {
      logger.warn('[AIContext] Failed to save opened documents:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- serializeTabForStorage is stable, defined earlier with useCallback
  }, [openedDocuments, activeProject]);
  
  // Optimistic Indexing - Track pending edits for instant search
  const [pendingEdits, setPendingEdits] = useState<PendingDocumentEdit[]>([]);
  
  // WebSocket Connection State
  const [wsConnected, setWsConnected] = useState(false);
  
  // Track if we've already attempted auto-create to prevent duplicates
  const autoCreateAttemptedRef = useRef(false);
  // Track whether the user was authenticated when conversations were loaded
  const wasAuthenticatedRef = useRef(false);
  
  // Update pageContext persona when activeProject or persona changes
  // Link-based architecture: Document context is set via links, not aiMode
  useEffect(() => {
    const effectivePersonaId = activeProject?.personaId || userDefaultPersonaId;
    
    // Link-based architecture: Only update persona in pageContext
    // Document context linking is now handled explicitly via Link AI button
    // DO NOT auto-link documents based on aiMode - mode is derived from links
    logger.debug('[AIContext] Updating persona:', effectivePersonaId, 'projectPersona:', activeProject?.personaId, 'userDefault:', userDefaultPersonaId);
    setPageContext(prev => ({
      ...prev,
      personaId: effectivePersonaId,
    }));
  }, [activeProject?.personaId, userDefaultPersonaId]);

  // API is the single source of truth for tab state - NO localStorage
  // This eliminates race conditions between localStorage, React state, and API

  // DEPRECATED: No longer uses localStorage - returns null, API is source of truth
  const loadActiveConversationFromStorage = useCallback(() => {
    // Removed localStorage - API preferences are loaded in loadConversations
    return null;
  }, []);

  // Save active conversation to API (no localStorage)
  // NOTE: This ONLY saves activeConversationId, NOT openConversationIds
  // Tab additions/removals are saved immediately in their respective functions
  const saveActiveConversationToStorage = useCallback((conversationId: string | null) => {
    // Only save activeConversationId - don't touch openConversationIds
    // This prevents overwriting tab state with stale data
    if (conversationId !== null) {
      userPreferencesApi.update({ activeConversationId: conversationId })
        .catch(err => logger.warn('[AIContext] Failed to save active conversation:', err));
    }
  }, []);

  const requestNavigation = useCallback(async (path: string): Promise<boolean> => {
    if (navigationHandler) {
      return await navigationHandler(path);
    }
    return true; // Allow navigation if no handler registered
  }, [navigationHandler]);

  const toggleChat = useCallback(() => {
    const newValue = !isOpen;
    setIsOpen(newValue);
    localStorage.setItem('aiChatOpen', newValue.toString());
  }, [isOpen]);

  const openChat = useCallback(() => {
    setIsOpen(true);
    localStorage.setItem('aiChatOpen', 'true');
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem('aiChatOpen', 'false');
  }, []);
  
  // WebSocket Connection Management
  const connectWebSocket = useCallback(async () => {
    try {
      const wsUrl = deriveWebSocketUrl();
      if (!wsUrl) {
        logger.warn('[AIContext] No WebSocket URL available');
        return;
      }
      
      const tokens = await authService.getTokens();
      const token = tokens?.accessToken || tokens?.idToken || '';
      
      if (!token) {
        logger.warn('[AIContext] No auth token available');
        return;
      }
      
      await websocketService.connect(wsUrl, token);
      logger.debug('[AIContext] WebSocket connected');
    } catch (error) {
      logger.error('[AIContext] WebSocket connection failed:', error);
    }
  }, []);
  
  const disconnectWebSocket = useCallback(() => {
    websocketService.close(false);
    logger.debug('[AIContext] WebSocket disconnected');
  }, []);
  
  // NOTE: registerStopGeneration and stopGeneration are now defined below
  // as conversation-scoped functions (after the buffer helpers)

  const loadConversations = useCallback(async (forceReload = false) => {
    try {
      logger.debug('[AIContext] ========== LOAD CONVERSATIONS START ==========');
      logger.debug('[AIContext] forceReload:', forceReload);
      logger.debug('[AIContext] Current state:', {
        conversations: conversations.length,
        activeConversationId,
        conversationsLoaded
      });
      
      let result;
      try {
        result = await conversationsApi.list(false); // Don't include archived
      } catch (apiError: any) {
        logger.error('[AIContext] ========== API CALL FAILED ==========');
        logger.error('[AIContext] API Error:', apiError);
        logger.error('[AIContext] Error message:', apiError.message);
        logger.error('[AIContext] Error response:', apiError.response?.data);
        throw apiError;
      }
      
      const data = result.conversations;
      setConversationsNextToken(result.nextToken);
      
      logger.debug('[AIContext] API returned conversations:', data?.length || 0, 'hasMore:', !!result.nextToken);
      logger.debug('[AIContext] API response type:', typeof data, 'isArray:', Array.isArray(data));
      
      // Validate API response
      if (!Array.isArray(data)) {
        const errorMsg = `API returned non-array response. Type: ${typeof data}, Value: ${JSON.stringify(data)}`;
        logger.error('[AIContext] CRITICAL ERROR:', errorMsg);
        throw new Error(errorMsg);
      }
      
      // Note: Empty conversations array is now valid (user may have deleted all)
      // API preferences are source of truth for tabs, not localStorage
      
      // SIMPLIFIED: Show ALL conversations, let user manage tabs
      // No scope filtering - user decides which conversations to keep visible
      const sorted = [...data].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      
      logger.debug(`[AIContext] Loaded ${sorted.length} total conversations`);
      
      // Load the active conversation from localStorage
      const savedActiveId = loadActiveConversationFromStorage();
      logger.debug('[AIContext] Saved active ID:', savedActiveId);
      
      // SIMPLIFIED: API is the single source of truth for visible tabs
      // No localStorage - eliminates race conditions
      let finalTabIds: string[];
      let apiActiveId: string | null = null;
      
      // Check if we already have tabs in React state (not initial load)
      const currentVisibleTabs = visibleTabIdsRef.current;
      
      logger.debug('[AIContext] Tab loading decision:', {
        currentVisibleTabs: currentVisibleTabs.length,
        preferencesLoaded: preferencesLoadedRef.current,
      });
      
      if (currentVisibleTabs.length > 0) {
        // Already have tabs in React state - preserve them, just validate they still exist
        finalTabIds = currentVisibleTabs.filter(id => 
          sorted.some(c => c.conversationId === id)
        );
        logger.debug('[AIContext] Preserving existing tabs:', finalTabIds.length);
      } else {
        // No tabs in React state - always fetch from API to get latest
        // This handles both initial load and StrictMode double-mount
        
        try {
          const apiPrefs = await userPreferencesApi.get();
          logger.debug('[AIContext] Loaded preferences from API:', {
            openConversationIds: apiPrefs.openConversationIds,
            activeConversationId: apiPrefs.activeConversationId,
            tabCount: apiPrefs.openConversationIds?.length || 0
          });
          
          // Validate API preferences against actual conversations
          const rawTabIds = apiPrefs.openConversationIds || [];
          finalTabIds = rawTabIds.filter(id => 
            sorted.some(c => c.conversationId === id)
          );
          
          logger.debug('[AIContext] Validated tabs:', {
            raw: rawTabIds.length,
            valid: finalTabIds.length,
            removed: rawTabIds.filter(id => !finalTabIds.includes(id))
          });
          
          apiActiveId = apiPrefs.activeConversationId || null;
          
          // Load user's default persona for fallback when project doesn't have one
          if (apiPrefs.defaultPersonaId) {
            setUserDefaultPersonaId(apiPrefs.defaultPersonaId);
            logger.debug('[AIContext] Loaded default persona:', apiPrefs.defaultPersonaId);
          }
          
        } catch (apiError) {
          logger.warn('[AIContext] Failed to load preferences from API:', apiError);
          finalTabIds = [];
        }
      }
      
      // Determine active conversation - preserve current if exists, otherwise use saved
      // Priority: current React state > API preference > localStorage
      const currentActiveId = activeConversationIdRef.current;
      const effectiveSavedActiveId = currentActiveId || apiActiveId || savedActiveId;
      let conversationToLoad = effectiveSavedActiveId ? sorted.find(c => c.conversationId === effectiveSavedActiveId) : null;
      
      if (effectiveSavedActiveId && !conversationToLoad) {
        logger.warn('[AIContext] Saved active conversation not found:', effectiveSavedActiveId);
        // Only clear if it was from localStorage (not current state)
        if (!currentActiveId) {
          saveActiveConversationToStorage(null);
        }
        conversationToLoad = null;
      }
      
      // If we have a current active conversation, preserve it (don't override during reload)
      if (currentActiveId && sorted.some(c => c.conversationId === currentActiveId)) {
        logger.debug('[AIContext] Preserving current active conversation:', currentActiveId);
        // Don't change anything - keep current state
        conversationToLoad = sorted.find(c => c.conversationId === currentActiveId) || null;
      }
      
      // Only add active conversation to tabs if it was explicitly in API preferences
      // Do NOT auto-add based on localStorage activeConversationId alone
      // This prevents closed tabs from reappearing on reload
      // Note: finalTabIds is already loaded from API preferences, so if the conversation
      // was closed (removed from preferences), it won't be in finalTabIds and we shouldn't add it
      // The only exception is if we switch to it explicitly (e.g., via enableEdit)
      
      // SET ALL STATE ATOMICALLY
      setConversations(sorted);
      setVisibleTabIds(finalTabIds);
      
      // Pre-populate artifact colors for all linked conversations
      // This ensures document/diagram tabs show their colors immediately
      // without requiring the user to visit each linked conversation first
      const linkedArtifactIds = sorted
        .flatMap(c => (c.links || []).map(l => l.artifactId))
        .filter(Boolean);
      if (linkedArtifactIds.length > 0) {
        setArtifactColorMap(prev => {
          const newMap = new Map(prev);
          let added = 0;
          for (const artifactId of linkedArtifactIds) {
            if (!newMap.has(artifactId)) {
              const colorIndex = hashArtifactId(artifactId) % ARTIFACT_COLOR_POOL.length;
              newMap.set(artifactId, ARTIFACT_COLOR_POOL[colorIndex]);
              added++;
            }
          }
          if (added > 0) {
            logger.debug('[AIContext] Pre-populated artifact colors for', added, 'linked artifacts');
          }
          return added > 0 ? newMap : prev; // no-op if nothing changed
        });
      }
      
      if (conversationToLoad) {
        // Check if we're already on this conversation (don't reload during streaming)
        const isAlreadyActive = currentActiveId === conversationToLoad.conversationId;
        
        if (isAlreadyActive) {
          logger.debug('[AIContext] Already on conversation:', conversationToLoad.conversationId, '- skipping reload');
          // Don't reload - preserve current messages (especially during streaming)
        } else {
          logger.debug('[AIContext] Loading conversation:', conversationToLoad.conversationId);
          setActiveConversationId(conversationToLoad.conversationId);
          saveActiveConversationToStorage(conversationToLoad.conversationId);
          
          // Fetch full conversation with messages, populate buffer
          try {
            const convId = conversationToLoad.conversationId;
            const fullConversation = await conversationsApi.get(convId);
            const loadedMsgs = (fullConversation.messages || []).map(m => ({
              ...m,
              timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
            }));
            // Only load messages if buffer is not currently processing (guard against clobbering in-flight messages)
            setConversationBuffers(prev => {
              const existing = prev.get(convId);
              if (existing?.isProcessing) {
                logger.debug('[AIContext] Buffer for', convId, 'is processing during loadConversations -- keeping in-flight');
                return prev;
              }
              const next = new Map(prev);
              next.set(convId, { ...(existing || createDefaultBuffer(convId)), messages: loadedMsgs });
              return next;
            });
            
            // Restore conversation settings
            if (fullConversation.settings?.mode) {
              logger.debug('[AIContext] Restoring mode from conversation:', fullConversation.settings.mode);
              setAiMode(fullConversation.settings.mode as AIMode);
            }
            // Restore model overrides from persisted settings
            const initOverrides = fullConversation.settings?.modelOverrides;
            if (initOverrides && Object.keys(initOverrides).length > 0) {
              const validOverrides: ModelOverrides = {};
              for (const [mode, modelId] of Object.entries(initOverrides)) {
                if (modelId && isValidModelId(modelId)) {
                  validOverrides[mode as AIMode] = modelId;
                }
              }
              if (Object.keys(validOverrides).length > 0) {
                setConversationModelOverrides(prev => ({
                  ...prev,
                  [convId]: validOverrides,
                }));
              }
            }
            
            logger.debug(`[AIContext] Loaded ${fullConversation.messages?.length || 0} messages for conversation ${convId}`);
          } catch (_error) {
            logger.error(`[AIContext] Failed to load messages for conversation ${conversationToLoad.conversationId}:`, _error);
            // Ensure buffer exists even on error
            const convId = conversationToLoad.conversationId;
            setConversationBuffers(prev => {
              if (prev.has(convId)) return prev;
              const next = new Map(prev);
              next.set(convId, createDefaultBuffer(convId));
              return next;
            });
          }
        }
      } else if (!currentActiveId) {
        // Only clear if we don't already have an active conversation
        logger.debug('[AIContext] No active conversation (open from history or start new)');
        logger.debug('[AIContext] Visible tabs:', finalTabIds.length, ', Conversations available:', sorted.length);
        setActiveConversationId(null);
        // No buffer to clear -- messages derived from activeBuffer will be []
      }
      
      // Mark as loaded LAST, after all state is set
      setConversationsLoaded(true);
      
    } catch (error) {
      logger.error('[AIContext] ========== LOAD CONVERSATIONS FAILED ==========');
      logger.error('[AIContext] Error:', error);
      setConversationsLoaded(true); // Still mark as loaded so UI can show error state
      throw error; // Re-throw instead of swallowing
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally excluding state values that would cause infinite reload loops
  }, [loadActiveConversationFromStorage, saveActiveConversationToStorage]);

  const loadMoreConversations = useCallback(async () => {
    if (!conversationsNextToken || loadingMoreConversations) {
      logger.debug('[AIContext] No more conversations to load or already loading');
      return;
    }
    
    try {
      setLoadingMoreConversations(true);
      logger.debug('[AIContext] Loading more conversations with nextToken');
      
      const result = await conversationsApi.list(false, conversationsNextToken);
      const data = result.conversations;
      
      logger.debug('[AIContext] Loaded', data.length, 'more conversations, hasMore:', !!result.nextToken);
      
      // Append to existing conversations
      setConversations(prev => {
        const merged = [...prev, ...data];
        // Remove duplicates
        const unique = merged.filter((conv, index, self) => 
          self.findIndex(c => c.conversationId === conv.conversationId) === index
        );
        return unique.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      });
      
      // Pre-populate artifact colors for newly loaded linked conversations
      const linkedArtifactIds = data
        .flatMap(c => (c.links || []).map(l => l.artifactId))
        .filter(Boolean);
      if (linkedArtifactIds.length > 0) {
        setArtifactColorMap(prev => {
          const newMap = new Map(prev);
          let added = 0;
          for (const artifactId of linkedArtifactIds) {
            if (!newMap.has(artifactId)) {
              const colorIndex = hashArtifactId(artifactId) % ARTIFACT_COLOR_POOL.length;
              newMap.set(artifactId, ARTIFACT_COLOR_POOL[colorIndex]);
              added++;
            }
          }
          return added > 0 ? newMap : prev;
        });
      }
      
      setConversationsNextToken(result.nextToken);
    } catch (error) {
      logger.error('[AIContext] Failed to load more conversations:', error);
    } finally {
      setLoadingMoreConversations(false);
    }
  }, [conversationsNextToken, loadingMoreConversations]);

  const createConversation = useCallback(async (title?: string, context?: any): Promise<string> => {
    try {
      const contextToUse = context || pageContext;
      
      // Determine conversation scope based on active project workspace
      // If a project is active in the workspace, the conversation is project-scoped
      // Otherwise, it's global-scoped
      const scope: 'project' | 'global' = activeProject ? 'project' : 'global';
      const projectId = activeProject?.projectId || contextToUse.documentContext?.projectId;
      
      // Legacy scopeType for backwards compatibility
      const scopeType = contextToUse.documentContext?.documentNumber ? 'document' :
                        projectId ? 'project' :
                        'general';
      
      logger.debug('[AIContext] Creating conversation with scope:', {
        scope,
        scopeType,
        projectId,
        documentNumber: contextToUse.documentContext?.documentNumber,
        title: title || 'New Chat'
      });
      
      // Include allowedProjectIds - for project-scoped conversations, include the active project
      const conversationAllowedProjectIds = scope === 'project' && projectId 
        ? [projectId] 
        : [];
      
      const conversation = await conversationsApi.create({
        title: title || 'New Chat',
        settings: { model: selectedModel, mode: aiMode },
        // Top-level scope fields for new conversation model
        scope,
        projectId: scope === 'project' ? projectId : undefined,
        allowedProjectIds: conversationAllowedProjectIds,  // CRITICAL: Include project for tool context
        context: {
          ...contextToUse,
          scopeType,
          projectId,
          documentNumber: contextToUse.documentContext?.documentNumber,
        },
      });
      
      // Update local state with allowedProjectIds
      setAllowedProjectIds(conversationAllowedProjectIds);
      
      logger.debug('[AIContext] Conversation created:', conversation.conversationId);
      
      // CRITICAL: Update state immediately and atomically
      setActiveConversationId(conversation.conversationId);
      saveActiveConversationToStorage(conversation.conversationId);
      setConversations(prev => [conversation, ...prev]);
      // Create a fresh buffer for the new conversation
      setConversationBuffers(prev => {
        const next = new Map(prev);
        next.set(conversation.conversationId, createDefaultBuffer(conversation.conversationId));
        return next;
      });
      
      // Link-based architecture: New conversations are always unlinked
      // User must explicitly use "Link AI" button to link to an artifact
      setConversationLinks([]);
      logger.debug('[AIContext] New conversation created unlinked');
      
      // Add to visible tabs and save to API IMMEDIATELY
      setVisibleTabIds(prev => {
        const updated = [...prev, conversation.conversationId];
        
        logger.debug('[AIContext] Saving new conversation tabs to API:', {
          openConversationIds: updated,
          activeConversationId: conversation.conversationId
        });
        
        // IMMEDIATE save - ensures new tabs survive hard reload
        userPreferencesApi.update({ 
          openConversationIds: updated, 
          activeConversationId: conversation.conversationId 
        }).then(() => {
          logger.debug('[AIContext] Saved new conversation to API immediately - SUCCESS');
        }).catch(err => {
          logger.error('[AIContext] Failed to save new conversation:', err);
        });
        
        return updated;
      });
      
      return conversation.conversationId;
    } catch (error) {
      logger.error('Failed to create conversation:', error);
      throw error;
    }
  }, [selectedModel, aiMode, pageContext, activeProject, saveActiveConversationToStorage]);

  const switchConversation = useCallback(async (id: string, addToVisibleTabs = false) => {
    try {
      // Pure view switch: no stopping background generation.
      // The previous conversation (if streaming) keeps streaming into its own buffer.
      
      const conversation: AIConversation = await conversationsApi.get(id);
      
      // Only add to visible tabs if explicitly requested (e.g., from enableEdit flow)
      if (addToVisibleTabs) {
        setVisibleTabIds(prev => {
          if (prev.includes(id)) return prev;
          const updated = [...prev, id];
          
          // IMMEDIATE save - ensures tabs survive hard reload
          userPreferencesApi.update({ 
            openConversationIds: updated, 
            activeConversationId: id 
          }).then(() => {
            logger.debug('[AIContext] Saved tab switch to API immediately');
          }).catch(err => {
            logger.warn('[AIContext] Failed to save tab switch:', err);
          });
          
          logger.debug('[AIContext] Added conversation to visible tabs:', id);
          return updated;
        });
      }
      
      setActiveConversationId(id);
      saveActiveConversationToStorage(id);
      
      // Ensure buffer exists for this conversation, then populate messages
      // Only load from API if the buffer is NOT currently processing (to avoid clobbering in-flight messages)
      setConversationBuffers(prev => {
        const existing = prev.get(id);
        if (existing?.isProcessing) {
          // Buffer is active/streaming -- don't overwrite messages from API
          logger.debug('[AIContext] Buffer for', id, 'is processing -- keeping in-flight messages');
          return prev;
        }
        const next = new Map(prev);
        const messagesToSet = (conversation.messages || []).map(m => ({
          ...m,
          timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
        }));
        next.set(id, {
          ...(existing || createDefaultBuffer(id)),
          messages: messagesToSet,
        });
        return next;
      });
      
      // Sync messageCount from the full conversation response to the metadata list
      // The list endpoint may have a stale count; the GET/:id response has the latest from DynamoDB
      if (conversation.messageCount != null) {
        setConversations(prev => prev.map(c =>
          c.conversationId === id && c.messageCount !== conversation.messageCount
            ? { ...c, messageCount: conversation.messageCount }
            : c
        ));
      }
      
      // Restore conversation mode
      const modeToRestore: AIMode = (conversation.settings?.mode as AIMode) || 'ask';
      logger.debug('[AIContext] Restoring mode from conversation:', modeToRestore);
      setAiModeState(modeToRestore);
      
      // Restore model overrides from DynamoDB — selectedModel derives automatically
      const persistedOverrides = conversation.settings?.modelOverrides;
      if (persistedOverrides && Object.keys(persistedOverrides).length > 0) {
        // Filter out stale model IDs that no longer exist
        const validOverrides: ModelOverrides = {};
        for (const [mode, modelId] of Object.entries(persistedOverrides)) {
          if (modelId && isValidModelId(modelId)) {
            validOverrides[mode as AIMode] = modelId;
          } else if (modelId) {
            logger.warn(`[AIContext] Dropping stale model override: ${mode}=${modelId}`);
          }
        }
        if (Object.keys(validOverrides).length > 0) {
          setConversationModelOverrides(prev => ({
            ...prev,
            [id]: validOverrides,
          }));
        }
        logger.debug('[AIContext] Restored model overrides from DynamoDB:', validOverrides);
      }
      
      // CRITICAL: Restore document context from conversation
      // This enables auto-enable edit mode in WorkspacePage when switching to Agent mode conversations
      if (conversation.context?.documentNumber || conversation.context?.projectId) {
        logger.debug('[AIContext] Restoring document context from conversation:', {
          documentNumber: conversation.context.documentNumber,
          projectId: conversation.context.projectId,
          mode: conversation.settings?.mode
        });
        
        // Update pageContext with the document info from the conversation
        // This will trigger the auto-enable edit mode effect in WorkspacePage
        setPageContext(prev => ({
          ...prev,
          documentContext: {
            ...prev.documentContext,
            documentNumber: conversation.context.documentNumber,
            projectId: conversation.context.projectId,
          }
        }));
      }
      
      // Link-based architecture: restore conversation links
      if (conversation.links && Array.isArray(conversation.links)) {
        setConversationLinks(conversation.links);
        logger.debug('[AIContext] Restored conversation links:', conversation.links.length);
      } else {
        setConversationLinks([]);
      }
      
      // Bidirectional sync: Auto-focus document tab when switching to a conversation with a linked document/plan
      // Priority: 1) Conversation links (document or plan), 2) Document locks (legacy)
      const documentOrPlanLink = conversation.links?.find(l => l.artifactType === 'document' || l.artifactType === 'plan');
      if (documentOrPlanLink) {
        logger.debug('[AIContext] Conversation has linked document/plan, will auto-focus:', documentOrPlanLink.artifactId);
        pendingAutoFocusDocRef.current = documentOrPlanLink.artifactId;
      } else {
        // Fallback to lock-based auto-focus (legacy)
        const lock = documentLocks.find(l => l.lockedByConversationId === id);
        if (lock) {
          logger.debug('[AIContext] Conversation has document lock, will auto-focus:', lock.documentId);
          pendingAutoFocusDocRef.current = lock.documentId;
        } else {
          pendingAutoFocusDocRef.current = null;
        }
      }
      
      // NOTE: Edit mode is controlled exclusively by user action (clicking "Edit with AI" button)
      // Switching conversations does NOT auto-enter or exit edit mode
      // Backend links exist for conversation reuse, not for controlling edit state
      // The bidirectional sync only focuses tabs, it doesn't change edit mode
    } catch (error) {
      logger.error('Failed to switch conversation:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- MODE_DEFAULT_MODELS is stable constant
  }, [saveActiveConversationToStorage, conversationModelOverrides, documentLocks]);

  // Visible tab management - API is single source of truth (debounced save)
  const addVisibleTab = useCallback((id: string) => {
    setVisibleTabIds(prev => {
      if (prev.includes(id)) return prev;
      const updated = [...prev, id];
      
      // IMMEDIATE save - ensures linked tabs survive hard reload
      userPreferencesApi.update({ 
        openConversationIds: updated, 
        activeConversationId: id  // Also make it active
      }).then(() => {
        logger.debug('[AIContext] Saved tab addition to API immediately');
      }).catch(err => {
        logger.warn('[AIContext] Failed to save tab addition:', err);
      });
      
      logger.debug('[AIContext] Added visible tab:', id);
      return updated;
    });
  }, []);

  const removeVisibleTab = useCallback((id: string, fallbackToNext = true) => {
    // Release any document lock held by this conversation
    const lockHeldByConversation = documentLocks.find(l => l.lockedByConversationId === id);
    if (lockHeldByConversation) {
      logger.debug('[AIContext] Releasing document lock for closed conversation tab:', lockHeldByConversation.documentId);
      releaseDocumentLock(lockHeldByConversation.documentId);
      setActiveEditDocument(null);
    }
    
    setVisibleTabIds(prev => {
      const index = prev.indexOf(id);
      if (index === -1) return prev;
      
      const updated = prev.filter(tabId => tabId !== id);
      
      // IMMEDIATE save - don't debounce tab closures to prevent ghost tabs on reload
      const newActiveId = id === activeConversationIdRef.current 
        ? (updated[index] || updated[index - 1] || updated[0] || null)
        : activeConversationIdRef.current;
        
      userPreferencesApi.update({ 
        openConversationIds: updated, 
        activeConversationId: newActiveId 
      }).then(() => {
        logger.debug('[AIContext] Saved tab removal to API immediately');
      }).catch(err => {
        logger.warn('[AIContext] Failed to save tab removal:', err);
      });
      
      // Also clear from all localStorage keys to prevent ghost restoration
      if (id === activeConversationIdRef.current) {
        // Clear all possible localStorage keys
        localStorage.removeItem('activeConversationGeneral');
        // Clear any document/project specific keys that might match
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('activeConversation') && localStorage.getItem(key) === id) {
            localStorage.removeItem(key);
            logger.debug('[AIContext] Cleared localStorage key:', key);
          }
        });
      }
      
      logger.debug('[AIContext] Removed visible tab:', id);
      
      // If removing the active tab, switch to adjacent one
      if (fallbackToNext && id === activeConversationId && updated.length > 0) {
        const fallbackId = updated[index] || updated[index - 1] || updated[0];
        if (fallbackId) {
          setTimeout(() => switchConversation(fallbackId), 0);
        }
      } else if (fallbackToNext && id === activeConversationId && updated.length === 0) {
        setActiveConversationId(null);
        saveActiveConversationToStorage(null);
        // No buffer to clear -- with no active conversation, derived messages = []
      }
      
      // Clean up the buffer for the removed tab (stop generation if processing)
      setConversationBuffers(prev => {
        const existing = prev.get(id);
        if (!existing) return prev;
        // If conversation was processing, cancel it first
        if (existing.isProcessing) {
          const stopFn = stopGenerationRefs.current.get(id);
          if (stopFn) {
            stopFn().catch(err => logger.warn('[AIContext] Failed to stop generation for removed tab:', id, err));
          }
        }
        const next = new Map(prev);
        next.delete(id);
        stopGenerationRefs.current.delete(id);
        return next;
      });
      
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setActiveEditDocument is defined later but stable via useState
  }, [activeConversationId, switchConversation, saveActiveConversationToStorage, documentLocks, releaseDocumentLock]);

  const reorderVisibleTabs = useCallback((newOrder: string[]) => {
    setVisibleTabIds(newOrder);
    queuePreferencesSave(newOrder, activeConversationIdRef.current);
    logger.debug('[AIContext] Reordered visible tabs');
  }, [queuePreferencesSave]);

  const deleteConversation = useCallback(async (id: string, hard = false) => {
    try {
      await conversationsApi.delete(id, hard);
      setConversations(prev => prev.filter(c => c.conversationId !== id));
      
      // Remove from visible tabs and save to API
      setVisibleTabIds(prev => {
        const updated = prev.filter(tabId => tabId !== id);
        queuePreferencesSave(updated, activeConversationIdRef.current);
        return updated;
      });
      
      // Clean up the buffer for the deleted conversation (stop generation if processing)
      setConversationBuffers(prev => {
        const existing = prev.get(id);
        if (!existing) return prev;
        if (existing.isProcessing) {
          const stopFn = stopGenerationRefs.current.get(id);
          if (stopFn) {
            stopFn().catch(err => logger.warn('[AIContext] Failed to stop generation for deleted conversation:', id, err));
          }
        }
        const next = new Map(prev);
        next.delete(id);
        stopGenerationRefs.current.delete(id);
        return next;
      });
      
      if (activeConversationId === id) {
        setActiveConversationId(null);
        saveActiveConversationToStorage(null);
        
        // Link-based architecture: Clear conversation links when the linked conversation is deleted
        // This ensures no stale links remain
        setConversationLinks([]);
        logger.debug('[AIContext] Cleared conversation links (deleted linked conversation)');
      }
    } catch (error) {
      logger.error('Failed to delete conversation:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saveActiveConversationToStorage is stable
  }, [activeConversationId, queuePreferencesSave]);

  const renameConversation = useCallback(async (id: string, title: string) => {
    try {
      await conversationsApi.update(id, { title });
      setConversations(prev => prev.map(c => 
        c.conversationId === id ? { ...c, title } : c
      ));
    } catch (error) {
      logger.error('Failed to rename conversation:', error);
    }
  }, []);

  const updateConversationTitle = useCallback((id: string, title: string) => {
    // Update title in local state (used when WebSocket sends title update)
    logger.debug('[AIContext] updateConversationTitle called:', { id, title });
    setConversations(prev => {
      const updated = prev.map(c => 
        c.conversationId === id ? { ...c, title } : c
      );
      logger.debug('[AIContext] Conversations updated, found match:', prev.some(c => c.conversationId === id));
      return updated;
    });
  }, []);

  // ===== Buffer Helpers (hard-fail, no fallbacks) =====

  /** Get a buffer by conversationId -- throws if missing. Used by all mutation functions. */
  const getBuffer = useCallback((conversationId: string): ConversationBuffer => {
    const buffer = conversationBuffers.get(conversationId);
    if (!buffer) {
      throw new Error(`[AIContext] No buffer for conversation ${conversationId}. Call ensureBuffer() first.`);
    }
    return buffer;
  }, [conversationBuffers]);

  /** Ensure a buffer exists for the given conversationId. Creates one with defaults if missing. */
  const ensureBuffer = useCallback((conversationId: string) => {
    setConversationBuffers(prev => {
      if (prev.has(conversationId)) return prev;
      const next = new Map(prev);
      next.set(conversationId, createDefaultBuffer(conversationId));
      return next;
    });
  }, []);

  /** Generic helper: update a specific field in a conversation's buffer. Hard fails if buffer missing. */
  const updateBuffer = useCallback((conversationId: string, updater: (buf: ConversationBuffer) => ConversationBuffer) => {
    setConversationBuffers(prev => {
      const existing = prev.get(conversationId);
      if (!existing) {
        throw new Error(`[AIContext] No buffer for conversation ${conversationId} in updateBuffer.`);
      }
      const next = new Map(prev);
      next.set(conversationId, updater(existing));
      return next;
    });
  }, []);

  // ===== Derived State from Active Conversation Buffer =====

  const activeBuffer = useMemo<ConversationBuffer | null>(() => {
    if (!activeConversationId) return null;
    return conversationBuffers.get(activeConversationId) ?? null;
  }, [activeConversationId, conversationBuffers]);

  // These are "view" values derived from whichever conversation is active.
  // They are convenience properties so that components reading the context
  // don't need to know about buffers at all -- they just get the right state for the visible tab.
  const messages = useMemo(() => activeBuffer?.messages ?? [], [activeBuffer]);
  const isProcessing = useMemo(() => activeBuffer?.isProcessing ?? false, [activeBuffer]);
  const streamingProgress = useMemo(() => activeBuffer?.streamingProgress ?? null, [activeBuffer]);
  const thinkingPhase = useMemo<ThinkingPhase>(() => activeBuffer?.thinkingPhase ?? 'idle', [activeBuffer]);
  const thinkingActivities = useMemo(() => activeBuffer?.thinkingActivities ?? [], [activeBuffer]);
  const previewText = useMemo(() => activeBuffer?.previewText ?? '', [activeBuffer]);
  const debugState = useMemo(() => activeBuffer?.debugState ?? { chainCount: 0, turnCount: 0, elapsedMs: 0 }, [activeBuffer]);

  // True if ANY conversation buffer is currently processing (for global indicators like TopBar)
  const anyConversationProcessing = useMemo(() => {
    for (const buf of conversationBuffers.values()) {
      if (buf.isProcessing) return true;
    }
    return false;
  }, [conversationBuffers]);

  // ===== Conversation-Scoped Message Mutation Functions =====
  // All take a required conversationId -- no fallbacks.

  const addMessage = useCallback((conversationId: string, message: Message) => {
    updateBuffer(conversationId, buf => ({ ...buf, messages: [...buf.messages, message] }));
  }, [updateBuffer]);

  const updateLastMessage = useCallback((conversationId: string, content: string) => {
    updateBuffer(conversationId, buf => {
      if (buf.messages.length === 0) return buf;
      const newMessages = [...buf.messages];
      newMessages[newMessages.length - 1] = { ...newMessages[newMessages.length - 1], content };
      return { ...buf, messages: newMessages };
    });
  }, [updateBuffer]);

  const setLastMessageResolvedModel = useCallback((conversationId: string, resolvedModel: ResolvedModelInfo) => {
    updateBuffer(conversationId, buf => {
      if (buf.messages.length === 0) return buf;
      const lastMsg = buf.messages[buf.messages.length - 1];
      if (lastMsg.role === 'assistant') {
        return { ...buf, messages: [...buf.messages.slice(0, -1), { ...lastMsg, resolvedModel }] };
      }
      return buf;
    });
  }, [updateBuffer]);

  const markLastMessageStopped = useCallback((conversationId: string) => {
    updateBuffer(conversationId, buf => {
      if (buf.messages.length === 0) return buf;
      const lastMsg = buf.messages[buf.messages.length - 1];
      if (lastMsg.role === 'assistant') {
        return { ...buf, messages: [...buf.messages.slice(0, -1), { ...lastMsg, wasStopped: true }] };
      }
      return buf;
    });
  }, [updateBuffer]);

  const removeMessagesFromIndex = useCallback((conversationId: string, idx: number) => {
    updateBuffer(conversationId, buf => ({ ...buf, messages: buf.messages.slice(0, idx) }));
  }, [updateBuffer]);

  const removeLastMessage = useCallback((conversationId: string) => {
    updateBuffer(conversationId, buf => ({ ...buf, messages: buf.messages.slice(0, -1) }));
  }, [updateBuffer]);

  const clearMessages = useCallback((conversationId: string) => {
    updateBuffer(conversationId, buf => ({ ...buf, messages: [] }));
  }, [updateBuffer]);

  const prependMessages = useCallback((conversationId: string, newMessages: Message[]) => {
    updateBuffer(conversationId, buf => {
      const existingIds = new Set(buf.messages.map(m => (m as any).id).filter(Boolean));
      const filtered = newMessages.filter(m => !(m as any).id || !existingIds.has((m as any).id));
      return { ...buf, messages: [...filtered, ...buf.messages] };
    });
  }, [updateBuffer]);

  const setBufferMessages = useCallback((conversationId: string, msgs: Message[]) => {
    updateBuffer(conversationId, buf => ({ ...buf, messages: msgs }));
  }, [updateBuffer]);

  // ===== Conversation-Scoped AI State Mutation Functions =====

  const setIsProcessing = useCallback((conversationId: string, processing: boolean) => {
    updateBuffer(conversationId, buf => ({ ...buf, isProcessing: processing }));
  }, [updateBuffer]);

  const setStreamingProgress = useCallback((conversationId: string, progress: StreamingProgress | null | ((prev: StreamingProgress | null) => StreamingProgress | null)) => {
    updateBuffer(conversationId, buf => {
      const newProgress = typeof progress === 'function' ? progress(buf.streamingProgress) : progress;
      return { ...buf, streamingProgress: newProgress };
    });
  }, [updateBuffer]);

  const setThinkingPhase = useCallback((conversationId: string, phase: ThinkingPhase) => {
    updateBuffer(conversationId, buf => ({ ...buf, thinkingPhase: phase }));
  }, [updateBuffer]);

  const addThinkingActivity = useCallback((conversationId: string, activity: ThinkingActivity) => {
    updateBuffer(conversationId, buf => ({
      ...buf,
      thinkingActivities: [...buf.thinkingActivities, activity],
    }));
  }, [updateBuffer]);

  const updateThinkingActivities = useCallback((conversationId: string, updater: (prev: ThinkingActivity[]) => ThinkingActivity[]) => {
    updateBuffer(conversationId, buf => ({
      ...buf,
      thinkingActivities: updater(buf.thinkingActivities),
    }));
  }, [updateBuffer]);

  const clearThinkingActivities = useCallback((conversationId: string) => {
    updateBuffer(conversationId, buf => ({ ...buf, thinkingActivities: [] }));
  }, [updateBuffer]);

  const setPreviewText = useCallback((conversationId: string, text: string) => {
    updateBuffer(conversationId, buf => ({ ...buf, previewText: text }));
  }, [updateBuffer]);

  const setDebugState = useCallback((conversationId: string, state: { chainCount: number; turnCount: number; elapsedMs: number }) => {
    updateBuffer(conversationId, buf => ({ ...buf, debugState: state }));
  }, [updateBuffer]);

  // ===== Stop Generation -- per-conversation =====

  const registerStopGeneration = useCallback((conversationId: string, fn: (() => Promise<void>) | null) => {
    if (fn) {
      stopGenerationRefs.current.set(conversationId, fn);
    } else {
      stopGenerationRefs.current.delete(conversationId);
    }
  }, []);

  const stopGeneration = useCallback(async (conversationId: string) => {
    const fn = stopGenerationRefs.current.get(conversationId);
    if (fn) {
      await fn();
    } else {
      logger.warn(`[AIContext] No stopGeneration registered for conversation ${conversationId}`);
    }
  }, []);
  
  const updateDocumentScopes = useCallback(async (scopes: string[], projectIds: string[]) => {
    setAllowedScopes(scopes);
    setAllowedProjectIds(projectIds);
    
    // Update current conversation with new scopes
    if (activeConversationId) {
      try {
        await conversationsApi.update(activeConversationId, {
          allowedScopes: scopes,
          allowedProjectIds: projectIds,
        });
        
        logger.debug('Updated conversation scopes:', {
          conversationId: activeConversationId,
          allowedScopes: scopes,
          allowedProjectIds: projectIds,
        });
      } catch (error) {
        logger.error('Failed to update conversation scopes:', error);
      }
    }
  }, [activeConversationId]);
  
  // Opened Documents Management
  // Returns the new tabId (or existing tabId if already open)
  const addOpenedDocument = useCallback((doc: any, options?: { insertAfterTabId?: number; setActive?: boolean }) => {
    let newTabId: number | null = null;
    
    setOpenedDocuments(prev => {
      // Check if already open - match by id OR documentNumber (for document tabs)
      const existingIndex = prev.findIndex(d => 
        d.id === doc.id || 
        (doc.documentNumber && d.documentNumber && d.documentNumber === doc.documentNumber)
      );
      if (existingIndex !== -1) {
        const existing = prev[existingIndex];
        newTabId = existing.tabId;
        // Update existing document with any new properties (e.g., canEdit, status)
        // This ensures reopening a document with different options works correctly
        const updatedDoc = { ...existing, ...doc, tabId: existing.tabId };
        const result = [...prev];
        result[existingIndex] = updatedDoc;
        return result;
      }
      
      // Generate new tabId
      const maxTabId = prev.length > 0 ? Math.max(...prev.map(d => d.tabId)) : 0;
      newTabId = maxTabId + 1;
      
      // Ensure tabType is set (default to 'document' for backwards compatibility)
      const tabType = doc.tabType || (doc.panelType ? 'page' : 'document');
      const newDoc = { ...doc, tabId: newTabId, tabType };
      
      // Insert after specific tab if requested
      if (options?.insertAfterTabId !== undefined) {
        const insertIndex = prev.findIndex(d => d.tabId === options.insertAfterTabId);
        if (insertIndex !== -1) {
          const result = [...prev];
          result.splice(insertIndex + 1, 0, newDoc);
          return result;
        }
      }
      
      // Default: append to end
      return [...prev, newDoc];
    });
    
    // Set active if requested (need to use setTimeout to ensure state is updated)
    if (options?.setActive && newTabId !== null) {
      setTimeout(() => setActiveTabId(newTabId), 0);
    }
    
    return newTabId;
  }, []);
  
  // Track active tab ID for panels (shared state for sidebar navigation)
  // Initialize from localStorage to restore active tab on page reload
  const [activeTabId, setActiveTabId] = useState<number | null>(() => {
    try {
      // Check if there's an active project - if so, restore from project-specific storage
      const activeProjectSaved = localStorage.getItem('activeWorkspaceProject');
      
      if (activeProjectSaved) {
        try {
          const project = JSON.parse(activeProjectSaved);
          if (project?.projectId) {
            const projectKey = `workspace-tabs-${project.projectId}`;
            const projectSaved = localStorage.getItem(projectKey);
            if (projectSaved) {
              const parsed = JSON.parse(projectSaved);
              if (parsed.activeTabId !== undefined && parsed.activeTabId !== null) {
                logger.debug('[AIContext] Restored activeTabId from project storage:', parsed.activeTabId);
                return parsed.activeTabId;
              }
            }
          }
        } catch (e) {
          logger.warn('[AIContext] Failed to parse project storage for activeTabId:', e);
        }
      }
      
      // Fall back to global storage
      const saved = localStorage.getItem('aeroActiveTabId');
      if (saved) {
        const tabId = parseInt(saved);
        if (!isNaN(tabId)) {
          logger.debug('[AIContext] Restored activeTabId from localStorage:', tabId);
          return tabId;
        }
      }
    } catch (e) {
      logger.warn('[AIContext] Failed to restore activeTabId:', e);
    }
    return null;
  });
  
  // DERIVED STATE: activeEditDocument is derived from openedDocuments + activeTabId
  // This follows the clean separation: entity state (isEditing) + UI focus (activeTabId) = derived context
  const activeEditDocument = useMemo<ActiveEditDocumentState>(() => {
    const doc = openedDocuments.find(d => d.tabId === activeTabId && d.isEditing);
    if (!doc?.documentNumber || !doc?.projectId) return null;
    return {
      tabId: doc.tabId,
      documentNumber: doc.documentNumber,
      projectId: doc.projectId,
      jsonDocument: doc.jsonDocument,
      version: doc.version || '',
      s3VersionId: doc.s3VersionId,
      s3JsonKey: doc.s3JsonKey,
    };
  }, [openedDocuments, activeTabId]);
  
  // Persist activeTabId to project-specific or global storage
  useEffect(() => {
    try {
      if (activeProject && activeTabId !== null) {
        // Save to project-specific storage
        const workspaceKey = `workspace-tabs-${activeProject.projectId}`;
        const existing = localStorage.getItem(workspaceKey);
        if (existing) {
          const parsed = JSON.parse(existing);
          parsed.activeTabId = activeTabId;
          localStorage.setItem(workspaceKey, JSON.stringify(parsed));
        }
      } else if (activeTabId !== null) {
        // Save to global storage
        localStorage.setItem('aeroActiveTabId', String(activeTabId));
      }
    } catch (e) {
      logger.warn('[AIContext] Failed to save activeTabId:', e);
    }
  }, [activeTabId, activeProject]);
  
  // Auto-focus document tab when switching to a conversation with a linked document
  // This effect handles the deferred auto-focus set by switchConversation
  // NOTE: This only focuses the tab, it does NOT enter edit mode
  // Edit mode is controlled exclusively by user clicking "Edit with AI" button
  // Backend link exists for conversation reuse, not for auto-entering edit mode
  useEffect(() => {
    const docId = pendingAutoFocusDocRef.current;
    if (docId) {
      const docTab = openedDocuments.find(d => d.documentNumber === docId);
      if (docTab) {
        logger.debug('[AIContext] Auto-focusing document tab:', docId, 'tabId:', docTab.tabId);
        setActiveTabId(docTab.tabId);
        // Do NOT restore edit mode here - that's controlled by user action only
      }
      // Clear the pending ref after handling
      pendingAutoFocusDocRef.current = null;
    }
  }, [activeConversationId, openedDocuments]);
  
  // Bidirectional sync: Switch conversation when document tab changes
  // When user clicks a different document tab, switch to the conversation linked to that document
  const prevActiveTabIdRef = useRef<number | null>(null);
  useEffect(() => {
    // Skip if tab didn't actually change
    if (activeTabId === prevActiveTabIdRef.current) return;
    prevActiveTabIdRef.current = activeTabId;
    
    // Skip if no tab selected
    if (activeTabId === null) return;
    
    // Find the document for this tab
    const docTab = openedDocuments.find(d => d.tabId === activeTabId);
    if (!docTab || !docTab.documentNumber) return;
    
    // Find a visible conversation that's linked to this document (or plan)
    const linkedConversation = conversations.find(conv => {
      // Only check visible tabs for efficiency
      if (!visibleTabIds.includes(conv.conversationId)) return false;
      // Check if this conversation has a link to this document or plan
      // Plans use artifactType: 'plan', regular documents use 'document'
      return conv.links?.some(
        link => (link.artifactType === 'document' || link.artifactType === 'plan') && link.artifactId === docTab.documentNumber
      );
    });
    
    // If found and different from current, switch to that conversation
    if (linkedConversation && linkedConversation.conversationId !== activeConversationId) {
      logger.debug('[AIContext] Bidirectional sync: Tab switch triggered conversation switch', {
        docId: docTab.documentNumber,
        fromConversation: activeConversationId,
        toConversation: linkedConversation.conversationId
      });
      // Use setTimeout to avoid state update during render
      setTimeout(() => switchConversation(linkedConversation.conversationId), 0);
    }
    // NOTE: activeEditDocument is now DERIVED from openedDocuments + activeTabId
    // No need to manually update it - it automatically reflects the current state
  }, [activeTabId, openedDocuments, conversations, visibleTabIds, activeConversationId, switchConversation]);
  
  // Open a page panel as a tab and make it active
  const openPagePanel = useCallback((panelId: PagePanelId, searchQuery?: string) => {
    const panelTabId = `page-${panelId}`;
    
    const panelConfigs: Record<PagePanelId, { title: string; icon: string }> = {
      'reference-library': { title: 'Reference Library', icon: '📚' },
      'projects': { title: 'Projects', icon: '📁' },
      'company-docs': { title: 'Company Documents', icon: '🏢' },
      'doc-control': { title: 'Document Control', icon: '🗂️' },
      'quality': { title: 'Quality', icon: '📊' },
      'validation': { title: 'Validation', icon: '✓' },
      'crosswalk': { title: 'Crosswalk', icon: '🔀' },
      'suppliers': { title: 'Suppliers', icon: '🏭' },
      'users': { title: 'Users', icon: '👥' },
      'my-profile': { title: 'My Profile', icon: '👤' },
      'preferences': { title: 'Preferences', icon: '⚙️' },
      'organization': { title: 'Organization', icon: '🏢' },
      'billing': { title: 'Billing', icon: '💳' },
    };
    
    setOpenedDocuments(prev => {
      // Check if panel is already open
      const existing = prev.find(d => d.id === panelTabId);
      if (existing) {
        // Set active immediately since tab already exists
        setActiveTabId(existing.tabId);
        logger.debug('[AIContext] Focused existing page panel:', panelId, 'tabId:', existing.tabId);
        
        // Update search query if provided
        if (searchQuery !== undefined) {
          return prev.map(d => 
            d.id === panelTabId ? { ...d, searchQuery } : d
          );
        }
        return prev;
      }
      
      // Add new panel tab
      const maxTabId = prev.length > 0 ? Math.max(...prev.map(d => d.tabId)) : 0;
      const newTabId = maxTabId + 1;
      
      // Set active after a tick to ensure state update is processed
      setTimeout(() => {
        setActiveTabId(newTabId);
        logger.debug('[AIContext] Focused new page panel:', panelId, 'tabId:', newTabId);
      }, 0);
      
      const config = panelConfigs[panelId];
      
      return [...prev, {
        id: panelTabId,
        tabId: newTabId,
        title: config.title,
        tabType: 'page' as TabType,
        panelType: panelId,
        searchQuery,
      }];
    });
    
    logger.debug('[AIContext] Opened page panel:', panelId);
  }, []);
  
  // Switch project workspace (now that activeTabId and openedDocuments are defined)
  const switchProject = useCallback(async (projectId: string | null) => {
    logger.debug('[AIContext] Switching project workspace:', projectId);
    
    // Save current workspace state before switching
    if (activeProject) {
      const workspaceKey = `workspace-tabs-${activeProject.projectId}`;
      const currentState = {
        openTabs: openedDocuments.map(serializeTabForStorage),
        activeTabId,
        lastAccessedAt: new Date().toISOString(),
      };
      localStorage.setItem(workspaceKey, JSON.stringify(currentState));
      logger.debug('[AIContext] Saved workspace state for:', activeProject.projectId, openedDocuments.length, 'tabs');
    }
    
    if (!projectId) {
      // Switch to global mode
      setActiveProject(null);
      setWorkspaceScope('global');
      localStorage.removeItem('activeWorkspaceProject');
      
      // Clear project-specific tabs
      // activeEditDocument is derived and will be null when openedDocuments is empty
      setOpenedDocuments([]);
      setActiveTabId(null);
      
      logger.debug('[AIContext] Switched to global mode');
      return;
    }
    
    // Load project details
    try {
      const project = await projectsApi.get(projectId);
      const newActiveProject: ActiveProject = {
        projectId: project.id,
        projectCode: project.projectCode || project.id,
        projectTitle: project.projectTitle || project.name,
        customer: project.customerName || project.customer || '',
        customerId: project.customerId || '',
        personaId: project.personaId,  // AI persona for this project
        projectType: (() => {
          if (!project.projectType) {
            console.error('[AIContext] Project missing projectType', { projectId: project.id });
            throw new Error('Project has no projectType configured. Please set a project type in Project Settings.');
          }
          return project.projectType;
        })(),
      };
      
      setActiveProject(newActiveProject);
      setWorkspaceScope('project');
      localStorage.setItem('activeWorkspaceProject', JSON.stringify(newActiveProject));
      
      // Restore workspace state for this project
      const workspaceKey = `workspace-tabs-${projectId}`;
      try {
        const savedState = localStorage.getItem(workspaceKey);
        if (savedState) {
          const parsed = JSON.parse(savedState);
          const savedTabs = parsed.openTabs || [];
          logger.debug('[AIContext] Restoring workspace state for:', projectId, savedTabs.length, 'tabs');
          
          if (savedTabs.length > 0) {
            // Reconstruct document objects from saved tab data
            // The documents will be lazy-loaded when the user clicks on the tab
            const restoredDocs = savedTabs.map((tab: any) => ({
              id: tab.id || tab.documentId || `doc-${tab.tabId}`,
              tabId: tab.tabId,
              title: tab.title || 'Untitled',
              tabType: tab.tabType || 'document',
              documentNumber: tab.documentNumber || tab.documentId,
              projectId: tab.projectId || projectId,
              type: tab.type || 'project',
              // Page panel specific
              panelType: tab.panelType,
              searchQuery: tab.searchQuery,
              // These will be loaded when the tab is selected
              htmlContent: tab.htmlContent,
              canEdit: tab.canEdit,
              // Diagram-specific fields
              diagramId: tab.diagramId,
              // Mermaid/flowchart-specific fields
              mermaidDiagramId: tab.mermaidDiagramId,
              mermaidType: tab.mermaidType,
              // Coding project-specific fields
              codingProjectId: tab.codingProjectId,
            }));
            
            setOpenedDocuments(restoredDocs);
            setActiveTabId(parsed.activeTabId || (restoredDocs.length > 0 ? restoredDocs[0].tabId : null));
          } else {
            // No tabs in saved state
            setOpenedDocuments([]);
            setActiveTabId(null);
          }
        } else {
          // No saved state - start fresh
          setOpenedDocuments([]);
          setActiveTabId(null);
        }
      } catch (e) {
        logger.warn('[AIContext] Failed to restore workspace state:', e);
        setOpenedDocuments([]);
        setActiveTabId(null);
      }
      
      // NOTE: activeEditDocument is derived - it will automatically update based on openedDocuments
      
      logger.debug('[AIContext] Switched to project:', newActiveProject.projectCode);
    } catch (error) {
      logger.error('[AIContext] Failed to load project:', error);
      // Stay in current state on error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- serializeTabForStorage is stable, defined earlier
  }, [activeProject, openedDocuments, activeTabId]);
  
  const updateOpenedDocument = useCallback((tabId: number, updates: Partial<{ jsonDocument: any; version: string; s3VersionId: string; s3JsonKey: string }>) => {
    setOpenedDocuments(prev => prev.map(doc => 
      doc.tabId === tabId ? { ...doc, ...updates } : doc
    ));
  }, []);
  
  const removeOpenedDocument = useCallback((tabId: number) => {
    setOpenedDocuments(prev => prev.filter(d => d.tabId !== tabId));
    // NOTE: activeEditDocument is derived - it will automatically be null if this was the editing document
  }, []);
  
  const reorderOpenedDocuments = useCallback((fromIndex: number, toIndex: number) => {
    setOpenedDocuments(prev => {
      const result = [...prev];
      const [removed] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, removed);
      return result;
    });
  }, []);
  
  const clearOpenedDocuments = useCallback(() => {
    setOpenedDocuments([]);
    // No need to clear activeEditDocument - it's derived and will be null when openedDocuments is empty
  }, []);
  
  // Set a document to EDIT state (entity state change)
  // activeEditDocument is automatically derived from openedDocuments + activeTabId
  const setActiveEditDocument = useCallback((doc: any) => {
    if (doc?.tabId != null) {
      setOpenedDocuments(prev => prev.map(d => 
        d.tabId === doc.tabId ? { ...d, isEditing: true } : d
      ));
    }
  }, []);

  // Set the current active document to VIEW state (exit edit mode)
  // Uses activeTabId to find which document to update
  const clearActiveEditDocument = useCallback(() => {
    // Clear isEditing on the currently active document
    if (activeTabId != null) {
      setOpenedDocuments(prev => prev.map(d => 
        d.tabId === activeTabId ? { ...d, isEditing: false } : d
      ));
    }
  }, [activeTabId]);
  
  // Context Document Management Functions
  const addContextDocument = useCallback((doc: ContextDocument) => {
    setContextDocuments(prev => {
      // Don't add duplicates
      if (prev.some(d => d.id === doc.id)) {
        return prev;
      }
      logger.debug('[AIContext] Adding context document:', doc.title);
      return [...prev, doc];
    });
  }, []);
  
  const removeContextDocument = useCallback((id: string) => {
    setContextDocuments(prev => {
      logger.debug('[AIContext] Removing context document:', id);
      return prev.filter(d => d.id !== id);
    });
  }, []);
  
  const clearContextDocuments = useCallback(() => {
    logger.debug('[AIContext] Clearing all context documents');
    setContextDocuments([]);
  }, []);
  
  // Optimistic Indexing Functions
  const addPendingEdit = useCallback((edit: PendingDocumentEdit) => {
    logger.debug('[AIContext] Adding pending edit:', {
      documentNumber: edit.documentNumber,
      operationCount: edit.operations.length
    });
    
    setPendingEdits(prev => {
      // Remove existing edits for this document
      const filtered = prev.filter(e => e.documentNumber !== edit.documentNumber);
      // Add new edit
      return [...filtered, edit];
    });
    
    // Auto-clear after 30 seconds (fallback if re-index event doesn't fire)
    setTimeout(() => {
      clearPendingEdits(edit.documentNumber);
    }, 30000);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clearPendingEdits is defined below but stable; adding it causes forward-reference error
  }, []);
  
  const clearPendingEdits = useCallback((documentNumber: string) => {
    logger.debug('[AIContext] Clearing pending edits for:', documentNumber);
    setPendingEdits(prev => prev.filter(e => e.documentNumber !== documentNumber));
  }, []);
  
  const getPendingEdits = useCallback((documentNumber: string): any[] => {
    const edit = pendingEdits.find(e => e.documentNumber === documentNumber);
    return edit?.operations || [];
  }, [pendingEdits]);
  
  // Save mode changes to active conversation (model overrides are persisted in setSelectedModel)
  useEffect(() => {
    if (activeConversationId && conversationsLoaded) {
      conversationsApi.update(activeConversationId, {
        settings: {
          mode: effectiveMode,
          model: selectedModel,
          modelOverrides: conversationModelOverrides[activeConversationId] || {},
        },
      }).catch(error => {
        logger.error('[AIContext] Failed to save mode/model to conversation:', error);
      });
    }
  }, [effectiveMode, activeConversationId, conversationsLoaded, selectedModel, conversationModelOverrides]);

  // Removed: Message loading now handled in loadConversations() to prevent race conditions
  
  // DISABLED: Auto-create removed for debugging
  // If conversations.length === 0 for an authenticated user, log the error
  useEffect(() => {
    if (conversationsLoaded && conversations.length === 0 && !activeConversationId && !autoCreateAttemptedRef.current) {
      if (!wasAuthenticatedRef.current) {
        logger.debug('[AIContext] No conversations loaded - user is not authenticated (expected on login page)');
        return;
      }
      logger.error('[AIContext] ========== NO CONVERSATIONS LOADED ==========');
      logger.error('[AIContext] conversations.length:', conversations.length);
      logger.error('[AIContext] conversationsLoaded:', conversationsLoaded);
      logger.error('[AIContext] This indicates a backend filtering bug - check conversations handler logs');
    }
  }, [conversationsLoaded, conversations.length, activeConversationId]);
  
  // Load scopes from active conversation when switching
  useEffect(() => {
    if (activeConversationId && conversations.length > 0) {
      const activeConv = conversations.find(c => c.conversationId === activeConversationId);
      if (activeConv && activeConv.allowedScopes) {
        setAllowedScopes(activeConv.allowedScopes);
        setAllowedProjectIds(activeConv.allowedProjectIds || []);
      }
    }
  }, [activeConversationId, conversations]);
  
  // Clear buffer and switch when active conversation is filtered out by scope
  useEffect(() => {
    if (activeConversationId && conversations.length >= 0) {
      const isInScope = conversations.find(c => c.conversationId === activeConversationId);
      if (!isInScope && messages.length > 0) {
        logger.debug('[AIContext] Active conversation filtered out by scope, clearing buffer');
        // Clean up the buffer for the out-of-scope conversation
        setConversationBuffers(prev => {
          const next = new Map(prev);
          next.delete(activeConversationId);
          return next;
        });
        setActiveConversationId(null);
        saveActiveConversationToStorage(null);
        
        // Switch to first available conversation in scope if any exist
        if (conversations.length > 0) {
          const firstConv = conversations[0];
          logger.debug('[AIContext] Switching to first conversation in scope:', firstConv.title);
          switchConversation(firstConv.conversationId);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saveActiveConversationToStorage is stable, defined earlier
  }, [activeConversationId, conversations, messages.length, switchConversation]);
  
  // Load conversations on component mount
  // Simple approach: Load immediately on mount, don't wait for pageContext
  useEffect(() => {
    logger.debug('[AIContext] ========== MOUNT EFFECT TRIGGERED ==========');
    const loadInitial = async () => {
      try {
        logger.debug('[AIContext] Checking authentication...');
        const tokens = await authService.getTokens();
        logger.debug('[AIContext] Auth tokens:', { hasIdToken: !!tokens?.idToken, hasAccessToken: !!tokens?.accessToken });
        
        if (tokens?.idToken) {
          wasAuthenticatedRef.current = true;
          logger.debug('[AIContext] Authenticated - calling loadConversations');
          await loadConversations();
          logger.debug('[AIContext] loadConversations completed');
        } else {
          wasAuthenticatedRef.current = false;
          logger.debug('[AIContext] Not authenticated - skipping load, setting conversationsLoaded=true');
          setConversationsLoaded(true);
        }
      } catch (error) {
        logger.error('[AIContext] Failed to load conversations:', error);
        setConversationsLoaded(true);
      }
    };
    
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount
  
  // Reload when document or project context changes (not on initial mount, not during processing)
  const previousContextRef = useRef<{ doc?: string; proj?: string; page?: string }>({});
  useEffect(() => {
    const currentDoc = pageContext.documentContext?.documentNumber;
    const currentProj = pageContext.documentContext?.projectId;
    const currentPage = pageContext.pageName;
    
    const prev = previousContextRef.current;
    const hasChanged = prev.doc !== currentDoc || prev.proj !== currentProj || prev.page !== currentPage;
    const isInitialMount = !prev.doc && !prev.proj && !prev.page;
    
    // IMPORTANT: Don't reload during processing - this would overwrite the streaming response
    if (hasChanged && !isInitialMount && conversationsLoaded && !isProcessing) {
      logger.debug('[AIContext] Context changed, reloading conversations:', {
        from: prev,
        to: { doc: currentDoc, proj: currentProj, page: currentPage }
      });
      loadConversations();
    } else if (hasChanged && isProcessing) {
      logger.debug('[AIContext] Context changed but skipping reload (processing in progress)');
    }
    
    previousContextRef.current = { doc: currentDoc, proj: currentProj, page: currentPage };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageContext.documentContext?.documentNumber, pageContext.documentContext?.projectId, pageContext.pageName]);
  
  // WebSocket connection lifecycle - robust reconnection with React-level retry
  useEffect(() => {
    let reconnectTimer: NodeJS.Timeout | null = null;
    
    // Connect when chat opens and we're not connected
    if (isOpen && !wsConnected) {
      // Reset the service's internal reconnection state so a fresh cycle can begin
      // This handles the case where all internal retries were exhausted
      websocketService.resetReconnectionState();
      connectWebSocket().catch((error) => {
        logger.error('[AIContext] WebSocket connection failed, will retry:', error);
        // Schedule a React-level retry with delay
        // This is the fallback when the service's internal retries are exhausted
        reconnectTimer = setTimeout(() => {
          if (isOpen && !websocketService.isConnected()) {
            logger.debug('[AIContext] React-level reconnection retry');
            websocketService.resetReconnectionState();
            connectWebSocket().catch(err => {
              logger.error('[AIContext] React-level reconnection also failed:', err);
            });
          }
        }, 5000);
      });
    }
    
    // Listen for connection state changes
    const unsubscribe = websocketService.addConnectionListener((connected) => {
      setWsConnected(connected);
    });
    
    // Cleanup on unmount
    return () => {
      unsubscribe();
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      // Don't disconnect on unmount - let idle timeout handle it
      // This prevents disconnecting when components re-render
    };
  }, [isOpen, wsConnected, connectWebSocket]);

  return (
    <AIContext.Provider
      value={{
        isOpen,
        toggleChat,
        openChat,
        closeChat,
        chatWidth,
        setChatWidth,
        wsConnected,
        connectWebSocket,
        disconnectWebSocket,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        requestNavigation,
        setNavigationHandler,
        activeConversationId,
        conversations,
        setConversations,
        conversationsLoaded,
        hasMoreConversations: !!conversationsNextToken,
        loadingMoreConversations,
        loadConversations,
        loadMoreConversations,
        createConversation,
        switchConversation,
        deleteConversation,
        renameConversation,
        updateConversationTitle,
        visibleTabIds,
        addVisibleTab,
        removeVisibleTab,
        reorderVisibleTabs,
        messages,
        addMessage,
        updateLastMessage,
        setLastMessageResolvedModel,
        markLastMessageStopped,
        removeMessagesFromIndex,
        removeLastMessage,
        clearMessages,
        prependMessages,
        setBufferMessages,
        // Per-conversation buffers
        conversationBuffers,
        ensureBuffer,
        getBuffer,
        isProcessing,
        anyConversationProcessing,
        setIsProcessing,
        registerStopGeneration,
        stopGeneration,
        // Thinking overlay state (derived from active buffer)
        thinkingPhase,
        thinkingActivities,
        previewText,
        debugState,
        setThinkingPhase,
        addThinkingActivity,
        updateThinkingActivities,
        clearThinkingActivities,
        setPreviewText,
        setDebugState,
        aiMode,
        setAiMode,
        selectedModel,
        setSelectedModel,
        pageContext,
        setPageContext,
        streamingProgress,
        setStreamingProgress,
        referencedDocuments,
        setReferencedDocuments,
        onApplyEdits,
        setOnApplyEdits,
        allowedScopes,
        allowedProjectIds,
        updateDocumentScopes,
        // Link-based architecture
        conversationLinks,
        linkArtifact,
        unlinkArtifact,
        unlinkAllArtifacts,
        unlinkConversation,
        effectiveMode,
        hasLinks,
        pendingEdits,
        addPendingEdit,
        clearPendingEdits,
        getPendingEdits,
        openedDocuments,
        openPagePanel,
        activeTabId,
        setActiveTabId,
        activeEditDocument,
        addOpenedDocument,
        updateOpenedDocument,
        removeOpenedDocument,
        reorderOpenedDocuments,
        clearOpenedDocuments,
        setActiveEditDocument,
        clearActiveEditDocument,
        contextDocuments,
        addContextDocument,
        removeContextDocument,
        clearContextDocuments,
        diagramState,
        setDiagramState,
        activeDiagramId,
        setActiveDiagramId,
        refreshDiagramState,
        mermaidVersions,
        setMermaidVersion,
        codingProjectVersions,
        setCodingProjectVersion,
        activeProject,
        workspaceScope,
        switchProject,
        projectSwitcherOpen,
        openProjectSwitcher,
        closeProjectSwitcher,
        projectCreateModalOpen,
        openProjectCreateModal,
        closeProjectCreateModal,
        documentLocks,
        acquireDocumentLock,
        releaseDocumentLock,
        getDocumentLock,
        isDocumentLocked,
        isDocumentLockedByMe,
        transferDocumentLock,
        // Artifact color assignment
        artifactColorMap,
        assignArtifactColor,
        clearArtifactColors,
      }}
    >
      {children}
    </AIContext.Provider>
  );
}

export function useAI() {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI must be used within AIProvider');
  }
  return context;
}
